import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useScrollToTop, useScrollToTopOnChange } from '../utils/scrollUtils';
import { updateThemeColor } from '../utils/themeColor';
import {
  X,
  Star,
  MapPin,
  Clock,
  MessageCircle,
  Navigation,
  Heart,
  Share2,
  Search,
  Store,
  Car,
  ExternalLink,
  Camera,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Location, TrafficCamera } from '../types';
import AreaLineChart from './AreaLineChart';
import BusynessPredictionChart from './BusynessPredictionChart';
import { getLocalCurrentDateTime, useDateHandler, getDayOfWeek, DAYSOFWEEK } from '../utils/dates';
import DayOfWeekSelector from './DayOfWeekSelector';
import AIChatbot from './AIChatbot';
import { getParkingRecommendations, getMockParkingRecommendations } from '../services/openai';
import { fetchTrafficHazards, TrafficHazard } from '../services/traffic';
import ShopModal from './ShopModal';
import { getData } from '../utils/auth';
import ParkingLocationsModal from './ParkingLocationsModal';
import { eventLogger } from '../utils/auth';
import './Modal.css';
interface Shop {
  id: string;
  name: string;
  centreName?: string;
  category: string;
  latitude: number;
  longitude: number;
  address?: string;
  phone?: string;
  website?: string;
  openingHours?: string;
  description?: string;
  capacities?: { [key: string]: number };
  communityReviews?: Array<{
    id: string;
    userId: string;
    username: string;
    rating: number;
    comment: string;
    timestamp: Date;
  }>;
  parkingOptions?: Array<Map<string, any>>;
}

interface LocationModalProps {
  details: {
    _id?: string; // MongoDB _id
    name: string;
    details: any;
    parkingOptions?: any[];
  } | null;
  handleClose: () => void;
  shop?: any;
  onShopSelect?: (
    shopData: { name: string; centreName?: string; latitude?: number; longitude?: number } | string
  ) => void;
}

export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Reusable Live Status Card Component
export const LiveStatusCard = ({
  getCurrentCapacity,
  getParkTime,
  convertedCurrentDateTime,
  isShop = false,
  getDayCapacities,
  onViewSchedule,
}) => {
  const currentCapacity = getCurrentCapacity();
  const parkTime = getParkTime ? getParkTime(currentCapacity) : null;

  // Check if closed (capacity is 0, undefined, null, or very close to 0)
  const isClosed =
    currentCapacity === undefined || currentCapacity === null || currentCapacity === 0 || currentCapacity <= 0;

  const getBusynessColor = (capacity: number) => {
    if (capacity === undefined || capacity === null || capacity === 0) return '#64748b';
    if (capacity < 30) return '#10b981';
    if (capacity < 60) return '#f59e0b';
    if (capacity < 80) return '#fb923c';
    return '#ef4444';
  };

  const getBusynessText = (capacity: number) => {
    if (capacity === undefined || capacity === null || capacity === 0) return 'Closed';
    if (capacity < 30) return 'Very Quiet';
    if (capacity < 60) return 'Moderate';
    if (capacity < 80) return 'Busy';
    return 'Very Busy';
  };

  // Helper function to get next opening time
  const getNextOpeningTime = () => {
    if (!getDayCapacities) return null;

    const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const currentParts = convertedCurrentDateTime.split(' ');
    const currentDay = currentParts[0];
    const currentTime = currentParts[1];
    const currentDayIndex = allDays.indexOf(currentDay);

    // Search from current day onwards (up to 7 days)
    for (let i = 0; i < 7; i++) {
      const dayIndex = (currentDayIndex + i) % 7;
      const checkDay = allDays[dayIndex];
      const dayData = getDayCapacities(checkDay + ' 00:00:00');

      if (dayData && dayData.length > 0) {
        for (const [datetime, capacity] of dayData) {
          const slotTime = datetime.split(' ')[1];

          // If same day, only check times after current time
          if (i === 0 && slotTime <= currentTime) continue;

          if (capacity > 0) {
            const timeFormatted = slotTime.substring(0, 5); // HH:MM
            const dayLabel = i === 0 ? 'today' : i === 1 ? 'tomorrow' : checkDay;
            return {
              label: `${dayLabel} at ${timeFormatted}`,
              capacity: capacity,
              busynessText: getBusynessText(capacity),
            };
          }
        }
      }
    }
    return null;
  };

  // Helper function to get next best times to visit
  const getNextBestTimes = () => {
    if (!getDayCapacities) return [];

    const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const currentParts = convertedCurrentDateTime.split(' ');
    const currentDay = currentParts[0];
    const currentTime = currentParts[1];
    const currentDayIndex = allDays.indexOf(currentDay);

    const allUpcomingTimes: Array<{ time: string; capacity: number; day: string }> = [];

    // Collect times from next 2 days
    for (let i = 0; i < 2; i++) {
      const dayIndex = (currentDayIndex + i) % 7;
      const checkDay = allDays[dayIndex];
      const dayData = getDayCapacities(checkDay + ' 00:00:00');

      if (dayData && dayData.length > 0) {
        dayData.forEach(([datetime, capacity]) => {
          const slotTime = datetime.split(' ')[1];

          // If same day, only check times after current time
          if (i === 0 && slotTime <= currentTime) return;

          if (capacity > 0) {
            const timeFormatted = slotTime.substring(0, 5);
            const dayLabel = i === 0 ? 'Today' : 'Tomorrow';
            allUpcomingTimes.push({
              time: `${dayLabel} ${timeFormatted}`,
              capacity: capacity,
              day: checkDay,
            });
          }
        });
      }
    }

    // Sort by capacity (lowest first) and return top 3
    return allUpcomingTimes.sort((a, b) => a.capacity - b.capacity).slice(0, 3);
  };

  const nextOpening = isClosed ? getNextOpeningTime() : null;
  const bestTimes = isClosed ? getNextBestTimes() : [];

  return (
    <div
      className="current-busyness-alt"
      style={{ borderColor: isClosed ? '#cbd5e1' : getBusynessColor(currentCapacity) }}
    >
      {!isClosed ? (
        <>
          <div className="busyness-alt-header">
            <div className="busyness-alt-title">
              <h4>Live Status</h4>
              <span className="live-badge">
                <span className="live-indicator"></span>
                LIVE
              </span>
            </div>
            <div className="time-display">
              {convertedCurrentDateTime.substring(0, convertedCurrentDateTime.length - 3)}
            </div>
          </div>

          <div className="busyness-alt-main">
            <div className="capacity-section">
              <div
                className="capacity-circle"
                style={{
                  background: `conic-gradient(${getBusynessColor(
                    currentCapacity
                  )} ${currentCapacity}%, #f1f5f9 ${currentCapacity}%)`,
                }}
              >
                <div className="capacity-circle-inner">
                  <span className="capacity-number">{currentCapacity}%</span>
                  <span className="capacity-label">Busy</span>
                </div>
              </div>
              <div className="capacity-status" style={{ color: getBusynessColor(currentCapacity) }}>
                {getBusynessText(currentCapacity)}
              </div>
            </div>

            {parkTime && (
              <div className="quick-stats">
                <div className="quick-stat-item">
                  <div className="quick-stat-icon" style={{ background: '#7c3aed' }}>
                    {isShop ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"></path>
                        <circle cx="6.5" cy="16.5" r="2.5"></circle>
                        <circle cx="16.5" cy="16.5" r="2.5"></circle>
                      </svg>
                    )}
                  </div>
                  <div className="quick-stat-content">
                    <span className="quick-stat-label">{isShop ? 'Wait' : 'Park'} Time</span>
                    <span className="quick-stat-value">{parkTime}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="closed-content">
          <div className="closed-header">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M12 6v6l4 2"></path>
            </svg>
            <h3>Currently Closed</h3>
            <div className="time-display">
              {convertedCurrentDateTime.substring(0, convertedCurrentDateTime.length - 3)}
            </div>
          </div>

          {/* Next Opening */}
          {nextOpening && (
            <div className="next-opening">
              <div className="opening-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="12" r="10"></circle>
                </svg>
                Opens {nextOpening.label}
              </div>
              <p className="opening-subtitle">
                Expected: {nextOpening.busynessText} ({nextOpening.capacity}%)
              </p>
            </div>
          )}

          {/* Best Times */}
          {bestTimes.length > 0 && (
            <div className="closed-best-times">
              <p className="section-label">Best times when open:</p>
              <div className="closed-time-chips">
                {bestTimes.map((timeSlot, idx) => (
                  <div key={idx} className="mini-time-chip">
                    <span className="mini-time">{timeSlot.time}</span>
                    <span className="mini-capacity">{timeSlot.capacity}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Call to Action */}
          <button
            className="view-schedule-btn"
            onClick={() => {
              if (onViewSchedule) {
                onViewSchedule();
              } else {
                // Default behavior for LocationModal
                const crowdsTab = document.querySelector('[data-tab="busyness"]') as HTMLElement;
                if (crowdsTab) {
                  crowdsTab.click();
                  // Small delay to ensure tab is active before clicking Crowd Analyser (week tab, now first)
                  setTimeout(() => {
                    const crowdAnalyserBtn = document.querySelector('.busyness-sub-tab:nth-child(1)') as HTMLElement;
                    if (crowdAnalyserBtn) crowdAnalyserBtn.click();
                  }, 100);
                }
              }
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            View Full Schedule
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>

          {!nextOpening && <p className="no-data-hint">No schedule data available</p>}
        </div>
      )}
    </div>
  );
};

// Reusable Smart Visit Planner Component
export const SmartVisitPlanner = ({
  getDayCapacities,
  dayIdentifier,
  title = 'Smart Visit Planner',
  subtitle = 'Personalized recommendations for your visit',
}) => {
  const dayData = getDayCapacities(dayIdentifier);
  if (dayData.length === 0) return null;

  // Filter to only >0% values
  const validData = dayData.filter(([, capacity]) => Number(capacity) > 0);
  if (validData.length === 0) return null;

  // Sort by capacity
  const sortedByCapacity = [...validData].sort(([, a], [, b]) => Number(a) - Number(b));

  // Get absolute best times (lowest capacity) - up to 6 max
  const allQuietTimes = sortedByCapacity.slice(0, 6);

  // Find busiest period (highest capacity values)
  const busiestTimes = sortedByCapacity.slice(-6);

  // Within the busiest times, find the least busy options - up to 6 max
  const allPeakTimes = busiestTimes.sort(([, a], [, b]) => Number(a) - Number(b)).slice(0, 6);

  // Helper to format time as HH:MM
  const formatTime = (datetime: string) => {
    const timePart = datetime.split(' ')[1]; // Get "HH:MM:SS"
    return timePart.substring(0, 5); // Return "HH:MM"
  };

  // Time of day recommendations
  const morningTimes = validData
    .filter(([time]) => {
      const hour = parseInt(time.split(' ')[1].split(':')[0]);
      return hour >= 6 && hour < 12;
    })
    .sort(([, a], [, b]) => Number(a) - Number(b))
    .slice(0, 2);

  const afternoonTimes = validData
    .filter(([time]) => {
      const hour = parseInt(time.split(' ')[1].split(':')[0]);
      return hour >= 12 && hour < 17;
    })
    .sort(([, a], [, b]) => Number(a) - Number(b))
    .slice(0, 2);

  const eveningTimes = validData
    .filter(([time]) => {
      const hour = parseInt(time.split(' ')[1].split(':')[0]);
      return hour >= 17 && hour < 22;
    })
    .sort(([, a], [, b]) => Number(a) - Number(b))
    .slice(0, 2);

  // Get worst times to avoid (highest capacity) - up to 6 max
  const allWorstTimes = sortedByCapacity.slice(-6).reverse();

  const getCapacityColor = (capacity: number) => {
    if (capacity < 30) return '#10b981';
    if (capacity < 60) return '#f59e0b';
    if (capacity < 80) return '#fb923c';
    return '#ef4444';
  };

  const getCapacityLabel = (capacity: number) => {
    if (capacity < 30) return 'Very Quiet';
    if (capacity < 60) return 'Moderate';
    if (capacity < 80) return 'Busy';
    return 'Very Busy';
  };

  return (
    <div className="suggested-times-v2">
      <div className="suggested-times-v2-header">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <circle cx="12" cy="12" r="6"></circle>
          <circle cx="12" cy="12" r="2"></circle>
        </svg>
        <div>
          <h4>{title}</h4>
          <p>{subtitle}</p>
        </div>
      </div>

      {/* Absolute Best Times */}
      <div className="recommendation-section">
        <div className="section-title best">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
          </svg>
          <span>Top Quietest Times</span>
        </div>
        <div className="time-chips-scroll">
          {allQuietTimes.map(([time, capacity]) => (
            <div key={time} className="time-chip best">
              <span className="chip-time">{formatTime(time)}</span>
              <span className="chip-capacity">{capacity}%</span>
              <span className="chip-label">{getCapacityLabel(Number(capacity))}</span>
              <div
                className="chip-bar"
                style={{
                  width: `${capacity}%`,
                  backgroundColor: getCapacityColor(Number(capacity)),
                }}
              ></div>
            </div>
          ))}
        </div>
      </div>

      {/* Least Busy During Peak */}
      {allPeakTimes.length > 0 && (
        <div className="recommendation-section">
          <div className="section-title peak">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
            </svg>
            <span>Best Options During Peak Hours</span>
          </div>
          <div className="time-chips-scroll">
            {allPeakTimes.map(([time, capacity]) => (
              <div key={time} className="time-chip peak">
                <span className="chip-time">{formatTime(time)}</span>
                <span className="chip-capacity">{capacity}%</span>
                <span className="chip-label">{getCapacityLabel(Number(capacity))}</span>
                <div
                  className="chip-bar"
                  style={{
                    width: `${capacity}%`,
                    backgroundColor: getCapacityColor(Number(capacity)),
                  }}
                ></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Time of Day Recommendations */}
      <div className="recommendation-section">
        <div className="section-title time-of-day">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
          <span>Best Times by Period</span>
        </div>

        <div className="time-periods-scroll">
          {morningTimes.length > 0 && (
            <div className="period-card morning">
              <div className="period-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="5"></circle>
                  <line x1="12" y1="1" x2="12" y2="3"></line>
                  <line x1="12" y1="21" x2="12" y2="23"></line>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                  <line x1="1" y1="12" x2="3" y2="12"></line>
                  <line x1="21" y1="12" x2="23" y2="12"></line>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                </svg>
              </div>
              <h5>Morning</h5>
              <p className="period-range">6am - 12pm</p>
              <div className="period-times">
                {morningTimes.map(([time, capacity]) => (
                  <div key={time} className="period-time">
                    <span className="period-time-value">{formatTime(time)}</span>
                    <span className="period-capacity">{capacity}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {afternoonTimes.length > 0 && (
            <div className="period-card afternoon">
              <div className="period-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="4"></circle>
                  <path d="M12 2v2"></path>
                  <path d="M12 20v2"></path>
                  <path d="m4.93 4.93 1.41 1.41"></path>
                  <path d="m17.66 17.66 1.41 1.41"></path>
                  <path d="M2 12h2"></path>
                  <path d="M20 12h2"></path>
                  <path d="m6.34 17.66-1.41 1.41"></path>
                  <path d="m19.07 4.93-1.41 1.41"></path>
                </svg>
              </div>
              <h5>Afternoon</h5>
              <p className="period-range">12pm - 5pm</p>
              <div className="period-times">
                {afternoonTimes.map(([time, capacity]) => (
                  <div key={time} className="period-time">
                    <span className="period-time-value">{formatTime(time)}</span>
                    <span className="period-capacity">{capacity}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {eveningTimes.length > 0 && (
            <div className="period-card evening">
              <div className="period-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                </svg>
              </div>
              <h5>Evening</h5>
              <p className="period-range">5pm - 10pm</p>
              <div className="period-times">
                {eveningTimes.map(([time, capacity]) => (
                  <div key={time} className="period-time">
                    <span className="period-time-value">{formatTime(time)}</span>
                    <span className="period-capacity">{capacity}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Worst Times to Avoid */}
      {allWorstTimes.length > 0 && (
        <div className="recommendation-section">
          <div className="section-title avoid">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            <span>Times to Avoid</span>
          </div>
          <div className="time-chips-scroll">
            {allWorstTimes.map(([time, capacity]) => (
              <div key={time} className="time-chip avoid">
                <span className="chip-time">{formatTime(time)}</span>
                <span className="chip-capacity">{capacity}%</span>
                <span className="chip-label">{getCapacityLabel(Number(capacity))}</span>
                <div
                  className="chip-bar"
                  style={{
                    width: `${capacity}%`,
                    backgroundColor: getCapacityColor(Number(capacity)),
                  }}
                ></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Reusable Crowd Level Chart Component
export const CrowdLevelChart = ({
  convertedCurrentDateTime,
  getDayCapacities,
  selectedDay: externalSelectedDay,
  setSelectedDay: externalSetSelectedDay,
  style,
}: {
  convertedCurrentDateTime: string;
  getDayCapacities: (dayIdentifier: string) => Array<[string, number]>;
  selectedDay?: string;
  setSelectedDay?: (day: string) => void;
  style?: React.CSSProperties;
}) => {
  // Use external state if provided, otherwise manage internal state
  const currentDay = convertedCurrentDateTime.split(' ')[0];
  const [internalSelectedDay, setInternalSelectedDay] = React.useState<string>(currentDay);
  const selectedDay = externalSelectedDay !== undefined ? externalSelectedDay : internalSelectedDay;
  const setSelectedDay = externalSetSelectedDay || setInternalSelectedDay;
  const [selectedDateTimes, setSelectedDateTimes] = React.useState<Array<[string, number]>>([]);

  // Update selectedDateTimes when selectedDay changes
  React.useEffect(() => {
    if (getDayCapacities) {
      const dayData = getDayCapacities(`${selectedDay} 00:00:00`);
      setSelectedDateTimes(dayData);
    }
  }, [selectedDay, getDayCapacities]);

  // Update selectedDay when convertedCurrentDateTime changes (only if using internal state)
  React.useEffect(() => {
    if (externalSelectedDay === undefined) {
      const newCurrentDay = convertedCurrentDateTime.split(' ')[0];
      setInternalSelectedDay(newCurrentDay);
    }
  }, [convertedCurrentDateTime, externalSelectedDay]);

  // Helper functions for busyness
  const getBusynessLabel = (capacity: number): string => {
    if (capacity === undefined || capacity === null || capacity === 0) return 'Closed';
    if (capacity < 30) return 'Very Quiet';
    if (capacity < 60) return 'Moderate';
    if (capacity < 80) return 'Busy';
    return 'Very Busy';
  };

  const getBusynessColor = (capacity: number): string => {
    if (capacity === undefined || capacity === null || capacity === 0) return '#64748b';
    if (capacity < 30) return '#10b981';
    if (capacity < 60) return '#f59e0b';
    if (capacity < 80) return '#fb923c';
    return '#ef4444';
  };

  // Don't render if no data
  if (selectedDateTimes.length === 0) {
    return null;
  }

  // Get current capacity for current day
  const isCurrentDay = selectedDay === convertedCurrentDateTime.split(' ')[0];
  const currentTime = isCurrentDay ? convertedCurrentDateTime.split(' ')[1].substring(0, 5) : undefined;
  const matchingDataPoint = currentTime
    ? selectedDateTimes.find((item) => {
        const itemTime = item[0].split(' ')[1].substring(0, 5);
        return itemTime === currentTime;
      })
    : undefined;
  const currentCapacityValue = matchingDataPoint ? Number(matchingDataPoint[1]) : undefined;
  const currentBusynessLabel = currentCapacityValue !== undefined ? getBusynessLabel(currentCapacityValue) : null;
  const busynessColor = currentCapacityValue !== undefined ? getBusynessColor(currentCapacityValue) : '#64748b';

  return (
    <div className="busyness-prediction-section" style={style}>
      <div className="busyness-prediction-header">
        <h3 className="busyness-prediction-title">Crowd Level</h3>
        {/* Current capacity display - only show for current day */}
        {isCurrentDay && currentCapacityValue !== undefined && currentBusynessLabel && (
          <div className="busyness-prediction-current-card" style={{ borderColor: busynessColor }}>
            <span className="busyness-prediction-live-badge">
              <span className="live-indicator"></span>
              <span>LIVE</span>
            </span>
            <div className="busyness-prediction-current-content">
              <span className="busyness-prediction-current-label">Current:</span>
              <span className="busyness-prediction-current-value">
                {Math.round(currentCapacityValue)}% {currentBusynessLabel}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Day Selector Buttons */}
      <div className="busyness-prediction-day-selector">
        {DAYSOFWEEK.map((day) => {
          const dayAbbr = day.substring(0, 3).toLowerCase();
          return (
            <button
              key={day}
              className={`busyness-prediction-day-btn ${selectedDay === day ? 'active' : ''}`}
              onClick={() => setSelectedDay(day)}
            >
              {dayAbbr}
            </button>
          );
        })}
      </div>

      {/* Chart */}
      <div className="busyness-prediction-chart-container">
        <BusynessPredictionChart
          labels={selectedDateTimes.map((item) => item[0].split(' ')[1])}
          dataPoints={selectedDateTimes.map((item) => Number(item[1]))}
          currentTimeIndex={isCurrentDay ? currentTime : undefined}
          currentCapacity={currentCapacityValue}
        />
      </div>
    </div>
  );
};

export const OverviewTab = ({
  getCurrentCapacity,
  getBusynessColor,
  parkTime,
  bestTimeToVisit,
  locationDetails,
  isShop = null,
  convertedCurrentDateTime,
  getDayCapacities,
  onPlanVisit = null,
  onViewSchedule = null,
  onViewWeekSchedule = null,
  onViewQuieterTimes = null,
}) => {
  const [showFullWeeklyInsights, setShowFullWeeklyInsights] = React.useState(false);
  const [overviewWeekFilter, setOverviewWeekFilter] = React.useState<'all' | 'weekday' | 'weekend'>('all');
  const currentCapacity = getCurrentCapacity();

  // Helper to get weekly average capacity
  const getWeeklyAverage = () => {
    if (!getDayCapacities || !locationDetails?.capacities) return null;

    const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    let totalCapacity = 0;
    let count = 0;

    allDays.forEach((day) => {
      const dayData = getDayCapacities(`${day} 00:00:00`);
      dayData.forEach(([, capacity]) => {
        if (capacity > 0) {
          totalCapacity += capacity;
          count++;
        }
      });
    });

    return count > 0 ? Math.round(totalCapacity / count) : null;
  };

  // Helper to count open shops
  const getOpenShopsCount = () => {
    if (!locationDetails?.shops) return 0;

    const shops = locationDetails.shops;
    const currentParts = convertedCurrentDateTime.split(' ');
    if (currentParts.length < 2) return 0;

    const currentDay = currentParts[0];
    const currentTime = currentParts[1];

    let openCount = 0;

    Object.keys(shops).forEach((shopId) => {
      const shop = shops[shopId];
      if (!shop?.capacities) return;

      // Check if shop has capacity data for current time
      if (shop.capacities[currentDay]) {
        const timeSlot = shop.capacities[currentDay].find((slot) => slot.time === currentTime);
        if (timeSlot && timeSlot.capacity > 0) {
          openCount++;
        }
      }
    });

    return openCount;
  };

  const weeklyAverage = getWeeklyAverage();
  const isPeakTime = currentCapacity > 70;
  const totalShops = locationDetails?.shops ? Object.keys(locationDetails.shops).length : 0;
  const openShops = getOpenShopsCount();

  return (
    <motion.div
      key="overview"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-content"
    >
      <div className="overview-section">
        {/* Peak Hours Warning */}
        {isPeakTime && currentCapacity > 0 && (
          <div className="peak-hours-alert">
            <div className="alert-icon">⚠️</div>
            <div className="alert-content">
              <h4>Peak Hours</h4>
              <p>This location is very busy right now ({currentCapacity}%)</p>
              <button
                className="alert-action-btn"
                onClick={() => {
                  if (onViewQuieterTimes) {
                    onViewQuieterTimes();
                  } else {
                    // Fallback for LocationModal
                    const busynessTab = document.querySelector('[data-tab="busyness"]');
                    if (busynessTab) (busynessTab as HTMLElement).click();
                  }
                }}
              >
                See Quieter Times →
              </button>
            </div>
          </div>
        )}

        {/* Crowd Level Card */}
        <CrowdLevelChart
          convertedCurrentDateTime={convertedCurrentDateTime}
          getDayCapacities={getDayCapacities}
          style={{ marginTop: 0 }}
        />

        {/* At This Location */}
        {(totalShops > 0 || locationDetails?.parkingOptions?.length > 0) && (
          <div className="at-location-card">
            <h4>At This Location</h4>
            <div className="location-features">
              {totalShops > 0 && (
                <div
                  className="feature-item"
                  onClick={() => {
                    const shopsTab = document.querySelector('[data-tab="shops"]');
                    if (shopsTab) (shopsTab as HTMLElement).click();
                  }}
                >
                  <div className="feature-icon">🛍️</div>
                  <div className="feature-info">
                    <span className="feature-title">
                      {totalShops} {totalShops === 1 ? 'Shop' : 'Shops'}
                    </span>
                    {openShops > 0 && <span className="feature-subtitle">{openShops} currently open</span>}
                  </div>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </div>
              )}

              {locationDetails?.parkingOptions?.length > 0 && (
                <div
                  className="feature-item"
                  onClick={() => {
                    const parkingTab = document.querySelector('[data-tab="parking"]');
                    if (parkingTab) (parkingTab as HTMLElement).click();
                  }}
                >
                  <div className="feature-icon">🅿️</div>
                  <div className="feature-info">
                    <span className="feature-title">
                      {locationDetails.parkingOptions.length} Parking{' '}
                      {locationDetails.parkingOptions.length === 1 ? 'Option' : 'Options'}
                    </span>
                    <span className="feature-subtitle">View nearby parking</span>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </div>
              )}
            </div>
          </div>
        )}

        <button
          className="directions-button"
          onClick={() => {
            const lat = locationDetails?.latitude;
            const lon = locationDetails?.longitude;
            if (lat && lon) {
              const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`;
              console.log('🗺️ Opening Google Maps with URL:', googleMapsUrl);
              window.open(googleMapsUrl, '_blank');
            } else {
              console.error('❌ No coordinates available for directions');
              alert('Location coordinates not available for directions');
            }
          }}
          title="Open Google Maps with directions to this location"
        >
          <Navigation size={18} />
          <span>Get Directions</span>
        </button>

        {/* Next Best Times to Visit */}
        {(() => {
          if (!getDayCapacities) {
            return null;
          }

          const todayData = getDayCapacities(convertedCurrentDateTime);

          if (todayData.length === 0) return null;

          // Filter to only >0% values
          const validData = todayData.filter(([, capacity]) => Number(capacity) > 0);

          if (validData.length === 0) return null;

          // Get current time from convertedCurrentDateTime
          const [currentDatePart, currentTimePart] = convertedCurrentDateTime.split(' ');
          const currentHour = parseInt(currentTimePart.split(':')[0]);
          const currentMinute = parseInt(currentTimePart.split(':')[1]);

          // Filter times that are after current time (same day only)
          const futureTimes = validData.filter(([datetime]) => {
            const [datePart, timePart] = datetime.split(' ');
            const hour = parseInt(timePart.split(':')[0]);
            const minute = parseInt(timePart.split(':')[1]);

            // Only same day
            if (datePart !== currentDatePart) return false;

            // Check if time is after current time
            const timeInMinutes = hour * 60 + minute;
            const currentTimeInMinutes = currentHour * 60 + currentMinute;

            return timeInMinutes > currentTimeInMinutes;
          });

          if (futureTimes.length === 0) {
            // Show "No recommendations" message
            return (
              <div className="next-best-times-card">
                <div className="next-best-times-header">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <div>
                    <h4>Suggested Times to Visit</h4>
                    <p>Quietest times to visit from now</p>
                  </div>
                </div>
                <div className="no-recommendations-message">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <p>No more times available today</p>
                  <span>Plan ahead for the rest of the week</span>
                  <button
                    className="view-week-btn"
                    onClick={() => {
                      if (onViewWeekSchedule) {
                        onViewWeekSchedule();
                      } else {
                        // Default behavior for LocationModal
                        const crowdsTab = document.querySelector('[data-tab="busyness"]') as HTMLElement;
                        if (crowdsTab) {
                          crowdsTab.click();
                          // Small delay to ensure tab is active before clicking Crowd Analyser (week tab, now first)
                          setTimeout(() => {
                            const crowdAnalyserBtn = document.querySelector('.busyness-sub-tab:nth-child(1)') as HTMLElement;
                            if (crowdAnalyserBtn) crowdAnalyserBtn.click();
                          }, 100);
                        }
                      }
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    View This Week's Schedule
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              </div>
            );
          }

          // Sort by capacity and get 2 least busy upcoming times
          const nextBestTimes = futureTimes.sort(([, a], [, b]) => Number(a) - Number(b)).slice(0, 2);

          const formatTime = (datetime: string) => {
            const timePart = datetime.split(' ')[1];
            return timePart.substring(0, 5);
          };

          const getCapacityColor = (capacity: number) => {
            if (capacity < 30) return '#10b981';
            if (capacity < 60) return '#f59e0b';
            if (capacity < 80) return '#fb923c';
            return '#ef4444';
          };

          const getCapacityLabel = (capacity: number) => {
            if (capacity < 30) return 'Very Quiet';
            if (capacity < 60) return 'Moderate';
            if (capacity < 80) return 'Busy';
            return 'Very Busy';
          };

          return (
            <div className="next-best-times-card">
              <div className="next-best-times-header">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <div>
                  <h4>Suggested Times to Visit</h4>
                  <p>Quietest times to visit from now</p>
                </div>
              </div>

              <div className="next-times-chips">
                {nextBestTimes.map(([time, capacity]) => (
                  <div key={time} className="time-chip best">
                    <span className="chip-time">{formatTime(time)}</span>
                    <span className="chip-capacity">{capacity}%</span>
                    <div
                      className="chip-bar"
                      style={{
                        width: `${capacity}%`,
                        backgroundColor: getCapacityColor(Number(capacity)),
                      }}
                    ></div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Weekly Insights Card - Collapsible */}
        {(() => {
          // Collect all week data
          const allWeekData = [];
          const weekdayData = [];
          const weekendData = [];
          const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
          const weekendDays = ['Saturday', 'Sunday'];

          [...weekdays, ...weekendDays].forEach((day) => {
            const dayData = getDayCapacities(day);
            dayData.forEach(([datetime, capacity]) => {
              if (Number(capacity) > 0) {
                const dataPoint = {
                  day: day,
                  time: datetime.split(' ')[1],
                  capacity: Number(capacity),
                  datetime: datetime,
                };
                allWeekData.push(dataPoint);
                if (weekdays.includes(day)) {
                  weekdayData.push(dataPoint);
                } else {
                  weekendData.push(dataPoint);
                }
              }
            });
          });

          if (allWeekData.length === 0) return null;

          // Filter data based on selection
          const filteredData =
            overviewWeekFilter === 'weekday'
              ? weekdayData
              : overviewWeekFilter === 'weekend'
              ? weekendData
              : allWeekData;

          const sortedFilteredData = [...filteredData].sort((a, b) => a.capacity - b.capacity);

          // Best hours overall - absolute quietest times
          const bestHours = sortedFilteredData.slice(0, 6);

          // Peak alternatives
          const busiestTimes = sortedFilteredData.slice(-20);
          const peakAlternatives = busiestTimes.sort((a, b) => a.capacity - b.capacity).slice(0, 4);

          // Early bird specials - use filtered data
          const earlyBirdTimes = filteredData
            .filter((item) => {
              const hour = parseInt(item.time.split(':')[0]);
              return hour >= 6 && hour < 9;
            })
            .sort((a, b) => a.capacity - b.capacity)
            .slice(0, 3);

          // Evening options - use filtered data
          const eveningTimes = filteredData
            .filter((item) => {
              const hour = parseInt(item.time.split(':')[0]);
              return hour >= 18 && hour < 22;
            })
            .sort((a, b) => a.capacity - b.capacity)
            .slice(0, 3);

          // Lunch hour alternatives - use filtered data
          const lunchTimes = filteredData
            .filter((item) => {
              const hour = parseInt(item.time.split(':')[0]);
              return hour >= 11 && hour < 14;
            })
            .sort((a, b) => a.capacity - b.capacity)
            .slice(0, 3);

          const formatTime = (timeStr: string) => timeStr.substring(0, 5);

          const getCapacityColor = (capacity: number) => {
            if (capacity < 30) return '#10b981';
            if (capacity < 60) return '#f59e0b';
            if (capacity < 80) return '#fb923c';
            return '#ef4444';
          };

          return (
            <div className="weekly-insights-card collapsible">
              <button
                className="weekly-insights-toggle"
                onClick={() => setShowFullWeeklyInsights(!showFullWeeklyInsights)}
              >
                <div className="weekly-insights-toggle-content">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <circle cx="12" cy="12" r="6"></circle>
                    <circle cx="12" cy="12" r="2"></circle>
                  </svg>
                  <div>
                    <h4>Insights for This Week</h4>
                    <p>Smart recommendations for every schedule</p>
                  </div>
                </div>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    transform: showFullWeeklyInsights ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                  }}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>

              {showFullWeeklyInsights && (
                <div className="weekly-insights-content">
                  {/* Week Filter Toggle */}
                  <div className="overview-week-filter">
                    <button
                      className={`filter-btn ${overviewWeekFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setOverviewWeekFilter('all')}
                    >
                      All Week
                    </button>
                    <button
                      className={`filter-btn ${overviewWeekFilter === 'weekday' ? 'active' : ''}`}
                      onClick={() => setOverviewWeekFilter('weekday')}
                    >
                      Weekdays
                    </button>
                    <button
                      className={`filter-btn ${overviewWeekFilter === 'weekend' ? 'active' : ''}`}
                      onClick={() => setOverviewWeekFilter('weekend')}
                    >
                      Weekend
                    </button>
                  </div>

                  <div className="insights-grid">
                    {/* Peak Hour Alternatives */}
                    {peakAlternatives.length > 0 && (
                      <div className="insight-section peak-alternatives">
                        <div className="insight-section-header">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                          </svg>
                          <h5>Best During Peak Hours</h5>
                        </div>
                        <p className="insight-description">Can't avoid busy times? These are your best bets</p>
                        <div className="insight-times">
                          {peakAlternatives.map((slot) => (
                            <div key={slot.datetime} className="insight-time-chip peak">
                              <div className="chip-header">
                                <span className="chip-day">{slot.day.substring(0, 3)}</span>
                                <span className="chip-capacity">{slot.capacity}%</span>
                              </div>
                              <span className="chip-time">{formatTime(slot.time)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Early Bird Specials */}
                    {earlyBirdTimes.length > 0 && (
                      <div className="insight-section early-bird">
                        <div className="insight-section-header">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <circle cx="12" cy="12" r="5"></circle>
                            <line x1="12" y1="1" x2="12" y2="3"></line>
                            <line x1="12" y1="21" x2="12" y2="23"></line>
                            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                            <line x1="1" y1="12" x2="3" y2="12"></line>
                            <line x1="21" y1="12" x2="23" y2="12"></line>
                          </svg>
                          <h5>Early Bird Special</h5>
                        </div>
                        <p className="insight-description">Beat the crowds before 9am</p>
                        <div className="insight-times">
                          {earlyBirdTimes.map((slot) => (
                            <div key={slot.datetime} className="insight-time-chip early">
                              <div className="chip-header">
                                <span className="chip-day">{slot.day.substring(0, 3)}</span>
                                <span className="chip-capacity">{slot.capacity}%</span>
                              </div>
                              <span className="chip-time">{formatTime(slot.time)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Lunch Hour Alternatives */}
                    {lunchTimes.length > 0 && (
                      <div className="insight-section lunch-hour">
                        <div className="insight-section-header">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
                            <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
                            <line x1="6" y1="1" x2="6" y2="4"></line>
                            <line x1="10" y1="1" x2="10" y2="4"></line>
                            <line x1="14" y1="1" x2="14" y2="4"></line>
                          </svg>
                          <h5>Lunch Break Friendly</h5>
                        </div>
                        <p className="insight-description">Quick visit options 11am-2pm</p>
                        <div className="insight-times">
                          {lunchTimes.map((slot) => (
                            <div key={slot.datetime} className="insight-time-chip lunch">
                              <div className="chip-header">
                                <span className="chip-day">{slot.day.substring(0, 3)}</span>
                                <span className="chip-capacity">{slot.capacity}%</span>
                              </div>
                              <span className="chip-time">{formatTime(slot.time)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Evening Options */}
                    {eveningTimes.length > 0 && (
                      <div className="insight-section evening">
                        <div className="insight-section-header">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                          </svg>
                          <h5>Evening Visits</h5>
                        </div>
                        <p className="insight-description">After-work options after 6pm</p>
                        <div className="insight-times">
                          {eveningTimes.map((slot) => (
                            <div key={slot.datetime} className="insight-time-chip evening">
                              <div className="chip-header">
                                <span className="chip-day">{slot.day.substring(0, 3)}</span>
                                <span className="chip-capacity">{slot.capacity}%</span>
                              </div>
                              <span className="chip-time">{formatTime(slot.time)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Best Hours - Absolute Quietest */}
                    {bestHours.length > 0 && (
                      <div className="insight-section best-hours">
                        <div className="insight-section-header">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                          </svg>
                          <h5>Best Hours Overall</h5>
                        </div>
                        <p className="insight-description">The absolute quietest times to visit</p>
                        <div className="insight-times">
                          {bestHours.map((slot) => (
                            <div key={slot.datetime} className="insight-time-chip best">
                              <div className="chip-header">
                                <span className="chip-day">{slot.day.substring(0, 3)}</span>
                                <span className="chip-capacity">{slot.capacity}%</span>
                              </div>
                              <span className="chip-time">{formatTime(slot.time)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="weekly-insights-footer">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    <span>Find the perfect time that fits your schedule</span>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Link to Planner Button */}
        <button
          className="planner-link-btn"
          onClick={() => {
            if (onPlanVisit) {
              onPlanVisit();
            } else {
              // Default behavior for LocationModal
              const crowdsTab = document.querySelector('[data-tab="busyness"]') as HTMLElement;
              if (crowdsTab) {
                crowdsTab.click();
                // Small delay to ensure tab is active before clicking planner (now second tab)
                setTimeout(() => {
                  const plannerBtn = document.querySelector('.busyness-sub-tab:nth-child(2)') as HTMLElement;
                  if (plannerBtn) plannerBtn.click();
                }, 100);
              }
            }
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <div className="planner-link-content">
            <span className="planner-link-title">Plan Your Perfect Visit</span>
            <span className="planner-link-desc">Use advanced filters to find your ideal time</span>
          </div>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>

        {/* Quick Info Cards - Moved to bottom */}
        <div className="quick-info-grid">
          {locationDetails?.category && (
            <div className="info-card category">
              <div className="info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 7h-9"></path>
                  <path d="M14 17H5"></path>
                  <circle cx="17" cy="17" r="3"></circle>
                  <circle cx="7" cy="7" r="3"></circle>
                </svg>
              </div>
              <span className="info-label">Category</span>
              <span className="info-value">{locationDetails.category}</span>
            </div>
          )}

          {weeklyAverage !== null && (
            <div className="info-card average">
              <div className="info-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                </svg>
              </div>
              <span className="info-label">Weekly Average</span>
              <span className="info-value">{weeklyAverage}% busy</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const BusynessTab = ({
  convertedCurrentDateTime,
  getCurrentCapacity,
  getParkTime,
  getDayCapacities,
  capacities,
  selectedDay,
  setSelectedDay,
  selectedDateTimes,
  name,
  isShop = false,
}) => {
  const [busynessSubTab, setBusynessSubTab] = useState<'today' | 'week' | 'planner' | 'planner2'>('week');
  // Use scroll-to-top utility hook with automatic scrolling on sub-tab changes
  const { scrollToTop } = useScrollToTopOnChange([busynessSubTab]);
  const [weekFilter, setWeekFilter] = useState<'all' | 'weekday' | 'weekend'>('all');

  useEffect(() => {
    eventLogger('location_tab', { location: name, tab: `busyness ${busynessSubTab}` });
  }, [busynessSubTab]);

  // Planner filters
  const [plannerTimePreference, setPlannerTimePreference] = useState<
    'morning' | 'afternoon' | 'evening' | 'custom' | 'any'
  >('any');
  const [plannerDayPreference, setPlannerDayPreference] = useState<'weekday' | 'weekend' | 'specific' | 'any'>('any');
  const [plannerCrowdTolerance, setPlannerCrowdTolerance] = useState<'quiet' | 'moderate' | 'any'>('quiet');
  const [plannerSpecificDays, setPlannerSpecificDays] = useState<string[]>([]);
  const [plannerTimeRangeStart, setPlannerTimeRangeStart] = useState<number>(6); // 6am
  const [plannerTimeRangeEnd, setPlannerTimeRangeEnd] = useState<number>(22); // 10pm
  const [showCustomFilters, setShowCustomFilters] = useState<boolean>(false);

  // Planner 2 state
  const [showPreferencesModal, setShowPreferencesModal] = useState<boolean>(false);
  const [savedPreferences, setSavedPreferences] = useState({
    dayPreference: 'any' as 'weekday' | 'weekend' | 'specific' | 'any',
    timePreference: 'any' as 'morning' | 'afternoon' | 'evening' | 'custom' | 'any',
    crowdTolerance: 'quiet' as 'quiet' | 'moderate' | 'any',
    specificDays: [] as string[],
    timeRangeStart: 6,
    timeRangeEnd: 22,
    preset: null as string | null,
  });

  // Quick preset function
  const applyPreset = (preset: string) => {
    switch (preset) {
      case 'early-bird':
        setPlannerDayPreference('weekday');
        setPlannerTimePreference('morning');
        setPlannerTimeRangeStart(6);
        setPlannerTimeRangeEnd(12);
        setPlannerCrowdTolerance('quiet');
        setPlannerSpecificDays([]);
        break;
      case 'lunch-break':
        setPlannerDayPreference('any');
        setPlannerTimePreference('custom');
        setPlannerTimeRangeStart(12);
        setPlannerTimeRangeEnd(14);
        setPlannerCrowdTolerance('moderate');
        setPlannerSpecificDays([]);
        break;
      case 'after-work':
        setPlannerDayPreference('weekday');
        setPlannerTimePreference('custom');
        setPlannerTimeRangeStart(18);
        setPlannerTimeRangeEnd(20);
        setPlannerCrowdTolerance('moderate');
        setPlannerSpecificDays([]);
        break;
      case 'weekend-warrior':
        setPlannerDayPreference('weekend');
        setPlannerTimePreference('morning');
        setPlannerTimeRangeStart(6);
        setPlannerTimeRangeEnd(12);
        setPlannerCrowdTolerance('quiet');
        setPlannerSpecificDays([]);
        break;
    }
  };

  return (
    <motion.div
      key="busyness"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-content"
    >
      <div className="busyness-section">
        {/* Busyness Sub-tabs */}
        <div className="busyness-sub-tabs">
          <button
            className={`busyness-sub-tab ${busynessSubTab === 'week' ? 'active' : ''}`}
            onClick={() => {
              setBusynessSubTab('week');
            }}
          >
            Crowd Analyser
          </button>
          <button
            className={`busyness-sub-tab ${busynessSubTab === 'planner2' ? 'active' : ''}`}
            onClick={() => {
              setBusynessSubTab('planner2');
            }}
          >
            Vibe Planner
          </button>
        </div>

        {/* Today Tab - Disabled */}
        {false && busynessSubTab === 'today' && (
          <>
            {/* Today's Busyness Graph */}
            {(() => {
              const todayData = getDayCapacities(convertedCurrentDateTime);
              if (todayData.length > 0) {
                todayData.sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime());

                return (
                  <div className="busyness-graph-alt">
                    <div className="graph-alt-header">
                      <div className="graph-alt-title">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                        </svg>
                        <h4>Today's Crowd Level</h4>
                      </div>
                      <div className="graph-meta">
                        <span className="graph-date">{convertedCurrentDateTime.split(' ')[0]}</span>
                        <span className="graph-count">{todayData.length} readings</span>
                      </div>
                    </div>

                    <div className="graph-alt-content">
                      <AreaLineChart
                        labels={todayData.map((item) => item[0].split(' ')[1])}
                        dataPoints={todayData.map((item) => Number(item[1]))}
                      />
                    </div>

                    <div className="graph-alt-footer">
                      <div className="graph-insight">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="16" x2="12" y2="12"></line>
                          <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                        <span>View hourly patterns to find the best time</span>
                      </div>
                    </div>
                  </div>
                );
              } else {
                return (
                  <div className="no-data-message">
                    <p>No crowd data available for today ({convertedCurrentDateTime.split(' ')[0]})</p>
                    <p>
                      Available days:{' '}
                      {(() => {
                        const availableDays = new Set();
                        if (capacities && typeof capacities === 'object') {
                          Object.keys(capacities).forEach((key) => {
                            const date = new Date(key);
                            const day = date.getDay() === 0 ? 6 : date.getDay() - 1;
                            availableDays.add(
                              ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][day]
                            );
                          });
                        }
                        return Array.from(availableDays).join(', ');
                      })()}
                    </p>
                  </div>
                );
              }
            })()}

            {/* Smart Visit Planner */}
            <SmartVisitPlanner
              getDayCapacities={getDayCapacities}
              dayIdentifier={convertedCurrentDateTime}
              title="Smart Visit Planner"
              subtitle="Personalized recommendations for your visit"
            />
          </>
        )}

        {/* Week Tab */}
        {busynessSubTab === 'week' && (
          <>
            {/* Old chart and day filter disabled */}
            {false && (
              <>
                <DayOfWeekSelector currentDay={selectedDay} onSelect={setSelectedDay} />
                {selectedDateTimes.length > 0 ? (
                  <div className="busyness-graph-alt">
                    <div className="graph-alt-header">
                      <div className="graph-alt-title">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                        </svg>
                        <h4>{selectedDay}'s Pattern</h4>
                      </div>
                      <div className="graph-meta">
                        <span className="graph-date">{selectedDay}</span>
                        <span className="graph-count">{selectedDateTimes.length} readings</span>
                      </div>
                    </div>

                    <div className="graph-alt-content">
                      <AreaLineChart
                        labels={selectedDateTimes.map((item) => item[0].split(' ')[1])}
                        dataPoints={selectedDateTimes.map((item) => item[1])}
                      />
                    </div>

                    <div className="graph-alt-footer">
                      <div className="graph-insight">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="16" x2="12" y2="12"></line>
                          <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                        <span>Compare different days to find your ideal visit time</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-data-message">
                    <p>No crowd data available for {selectedDay}</p>
                    <p>Try selecting a different day or check if the location has capacity data.</p>
                  </div>
                )}
              </>
            )}

            {/* Busyness Prediction Chart - New Design */}
            <CrowdLevelChart
              convertedCurrentDateTime={convertedCurrentDateTime}
              getDayCapacities={getDayCapacities}
              selectedDay={selectedDay}
              setSelectedDay={setSelectedDay}
            />

            {/* Smart Visit Planner for Selected Day */}
            <SmartVisitPlanner
              getDayCapacities={getDayCapacities}
              dayIdentifier={selectedDay}
              title="Smart Visit Planner"
              subtitle={`Personalized recommendations for ${selectedDay}`}
            />

            {/* Experimental Best Times This Week Card V2 - DISABLED */}
            {false &&
              (() => {
                // Collect all data from all days
                const allWeekData = [];
                const weekdayData = [];
                const weekendData = [];
                const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
                const weekendDays = ['Saturday', 'Sunday'];

                // Collect all week data
                [...weekdays, ...weekendDays].forEach((day) => {
                  const dayData = getDayCapacities(day);
                  dayData.forEach(([datetime, capacity]) => {
                    if (Number(capacity) > 0) {
                      const dataPoint = {
                        day: day,
                        time: datetime.split(' ')[1],
                        capacity: Number(capacity),
                        datetime: datetime,
                      };
                      allWeekData.push(dataPoint);
                      if (weekdays.includes(day)) {
                        weekdayData.push(dataPoint);
                      } else {
                        weekendData.push(dataPoint);
                      }
                    }
                  });
                });

                if (allWeekData.length === 0) return null;

                // Sort by capacity
                const sortedAllData = [...allWeekData].sort((a, b) => a.capacity - b.capacity);
                const sortedWeekdayData = [...weekdayData].sort((a, b) => a.capacity - b.capacity);
                const sortedWeekendData = [...weekendData].sort((a, b) => a.capacity - b.capacity);

                // 1. Absolute best times across the week
                const absoluteBestTimes = sortedAllData.slice(0, 5);

                // 2. Best times for weekdays
                const bestWeekdayTimes = sortedWeekdayData.slice(0, 4);

                // 3. Best times for weekends
                const bestWeekendTimes = sortedWeekendData.slice(0, 4);

                // 4. Peak alternatives - find busiest times, then get least busy within those
                const busiestTimes = sortedAllData.slice(-20); // Get top 20 busiest
                const peakAlternatives = busiestTimes.sort((a, b) => a.capacity - b.capacity).slice(0, 4);

                // 5. Early bird specials (before 9am)
                const earlyBirdTimes = allWeekData
                  .filter((item) => {
                    const hour = parseInt(item.time.split(':')[0]);
                    return hour >= 6 && hour < 9;
                  })
                  .sort((a, b) => a.capacity - b.capacity)
                  .slice(0, 3);

                // 6. Evening options (after 6pm)
                const eveningTimes = allWeekData
                  .filter((item) => {
                    const hour = parseInt(item.time.split(':')[0]);
                    return hour >= 18 && hour < 22;
                  })
                  .sort((a, b) => a.capacity - b.capacity)
                  .slice(0, 3);

                // 7. Lunch hour alternatives (11am-2pm)
                const lunchTimes = allWeekData
                  .filter((item) => {
                    const hour = parseInt(item.time.split(':')[0]);
                    return hour >= 11 && hour < 14;
                  })
                  .sort((a, b) => a.capacity - b.capacity)
                  .slice(0, 3);

                const formatTime = (timeStr: string) => timeStr.substring(0, 5);

                const getCapacityColor = (capacity: number) => {
                  if (capacity < 30) return '#10b981';
                  if (capacity < 60) return '#f59e0b';
                  if (capacity < 80) return '#fb923c';
                  return '#ef4444';
                };

                return (
                  <div className="week-best-times-card">
                    <div className="week-best-times-header">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"></path>
                      </svg>
                      <div className="week-best-times-title">
                        <h4>Best Times This Week</h4>
                        <span className="week-subtitle">Optimal visiting times for weekdays and weekends</span>
                      </div>
                    </div>

                    <div className="week-sections-container">
                      {/* Weekday Section */}
                      {bestWeekdayTimes.length > 0 && (
                        <div className="week-section">
                          <div className="week-section-header">
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                              <line x1="16" y1="2" x2="16" y2="6"></line>
                              <line x1="8" y1="2" x2="8" y2="6"></line>
                              <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                            <h5>Weekdays</h5>
                            <span className="week-days-label">Mon - Fri</span>
                          </div>
                          <div className="week-times-list">
                            {bestWeekdayTimes.map((slot, index) => (
                              <div key={slot.datetime} className="week-time-item">
                                <div className="week-time-rank">{index + 1}</div>
                                <div className="week-time-info">
                                  <div className="week-time-row">
                                    <span className="week-day">{slot.day}</span>
                                    <span className="week-time">{slot.time}</span>
                                    <span className="week-percentage">{slot.capacity}%</span>
                                  </div>
                                  <div className="week-time-bar">
                                    <div
                                      className="week-time-fill"
                                      style={{
                                        width: `${slot.capacity}%`,
                                        backgroundColor: getCapacityColor(slot.capacity),
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Weekend Section */}
                      {bestWeekendTimes.length > 0 && (
                        <div className="week-section">
                          <div className="week-section-header">
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <circle cx="12" cy="12" r="10"></circle>
                              <path d="M12 6v6l4 2"></path>
                            </svg>
                            <h5>Weekend</h5>
                            <span className="week-days-label">Sat - Sun</span>
                          </div>
                          <div className="week-times-list">
                            {bestWeekendTimes.map((slot, index) => (
                              <div key={slot.datetime} className="week-time-item">
                                <div className="week-time-rank">{index + 1}</div>
                                <div className="week-time-info">
                                  <div className="week-time-row">
                                    <span className="week-day">{slot.day}</span>
                                    <span className="week-time">{slot.time}</span>
                                    <span className="week-percentage">{slot.capacity}%</span>
                                  </div>
                                  <div className="week-time-bar">
                                    <div
                                      className="week-time-fill"
                                      style={{
                                        width: `${slot.capacity}%`,
                                        backgroundColor: getCapacityColor(slot.capacity),
                                      }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="week-best-times-footer">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                        <polyline points="2 17 12 22 22 17"></polyline>
                        <polyline points="2 12 12 17 22 12"></polyline>
                      </svg>
                      <span>Plan ahead with the quietest times for weekdays and weekends</span>
                    </div>
                  </div>
                );
              })()}

            {/* Experimental Weekly Insights Card */}
            {(() => {
              // Collect all week data
              const allWeekData = [];
              const weekdayData = [];
              const weekendData = [];
              const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
              const weekendDays = ['Saturday', 'Sunday'];

              [...weekdays, ...weekendDays].forEach((day) => {
                const dayData = getDayCapacities(day);
                dayData.forEach(([datetime, capacity]) => {
                  if (Number(capacity) > 0) {
                    const dataPoint = {
                      day: day,
                      time: datetime.split(' ')[1],
                      capacity: Number(capacity),
                      datetime: datetime,
                    };
                    allWeekData.push(dataPoint);
                    if (weekdays.includes(day)) {
                      weekdayData.push(dataPoint);
                    } else {
                      weekendData.push(dataPoint);
                    }
                  }
                });
              });

              if (allWeekData.length === 0) return null;

              // Filter data based on selection
              const filteredData =
                weekFilter === 'weekday' ? weekdayData : weekFilter === 'weekend' ? weekendData : allWeekData;

              const sortedFilteredData = [...filteredData].sort((a, b) => a.capacity - b.capacity);

              // Peak alternatives - use filtered data
              const busiestTimes = sortedFilteredData.slice(-20);
              const peakAlternatives = busiestTimes.sort((a, b) => a.capacity - b.capacity).slice(0, 4);

              // Early bird specials - use filtered data
              const earlyBirdTimes = filteredData
                .filter((item) => {
                  const hour = parseInt(item.time.split(':')[0]);
                  return hour >= 6 && hour < 9;
                })
                .sort((a, b) => a.capacity - b.capacity)
                .slice(0, 3);

              // Evening options - use filtered data
              const eveningTimes = filteredData
                .filter((item) => {
                  const hour = parseInt(item.time.split(':')[0]);
                  return hour >= 18 && hour < 22;
                })
                .sort((a, b) => a.capacity - b.capacity)
                .slice(0, 3);

              // Lunch hour alternatives - use filtered data
              const lunchTimes = filteredData
                .filter((item) => {
                  const hour = parseInt(item.time.split(':')[0]);
                  return hour >= 11 && hour < 14;
                })
                .sort((a, b) => a.capacity - b.capacity)
                .slice(0, 3);

              // Best hours overall - absolute quietest times
              const bestHours = sortedFilteredData.slice(0, 6);

              const formatTime = (timeStr: string) => timeStr.substring(0, 5);

              const getCapacityColor = (capacity: number) => {
                if (capacity < 30) return '#10b981';
                if (capacity < 60) return '#f59e0b';
                if (capacity < 80) return '#fb923c';
                return '#ef4444';
              };

              return (
                <div className="weekly-insights-card">
                  <div className="weekly-insights-header">
                    <div className="weekly-insights-title-section">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                      <div className="weekly-insights-title">
                        <h4>Weekly Insights</h4>
                        <p>Smart recommendations for every schedule</p>
                      </div>
                    </div>
                    <div className="week-filter-toggle">
                      <button
                        className={`filter-btn ${weekFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setWeekFilter('all')}
                      >
                        All Week
                      </button>
                      <button
                        className={`filter-btn ${weekFilter === 'weekday' ? 'active' : ''}`}
                        onClick={() => setWeekFilter('weekday')}
                      >
                        Weekdays
                      </button>
                      <button
                        className={`filter-btn ${weekFilter === 'weekend' ? 'active' : ''}`}
                        onClick={() => setWeekFilter('weekend')}
                      >
                        Weekend
                      </button>
                    </div>
                  </div>

                  <div className="insights-grid">
                    {/* Peak Hour Alternatives */}
                    {peakAlternatives.length > 0 && (
                      <div className="insight-section peak-alternatives">
                        <div className="insight-section-header">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                          </svg>
                          <h5>Best During Peak Hours</h5>
                        </div>
                        <p className="insight-description">Can't avoid busy times? These are your best bets</p>
                        <div className="insight-times">
                          {peakAlternatives.map((slot) => (
                            <div key={slot.datetime} className="insight-time-chip peak">
                              <div className="chip-header">
                                <span className="chip-day">{slot.day.substring(0, 3)}</span>
                                <span className="chip-capacity">{slot.capacity}%</span>
                              </div>
                              <span className="chip-time">{formatTime(slot.time)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Early Bird Specials */}
                    {earlyBirdTimes.length > 0 && (
                      <div className="insight-section early-bird">
                        <div className="insight-section-header">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <circle cx="12" cy="12" r="5"></circle>
                            <line x1="12" y1="1" x2="12" y2="3"></line>
                            <line x1="12" y1="21" x2="12" y2="23"></line>
                            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                            <line x1="1" y1="12" x2="3" y2="12"></line>
                            <line x1="21" y1="12" x2="23" y2="12"></line>
                          </svg>
                          <h5>Early Bird Special</h5>
                        </div>
                        <p className="insight-description">Beat the crowds before 9am</p>
                        <div className="insight-times">
                          {earlyBirdTimes.map((slot) => (
                            <div key={slot.datetime} className="insight-time-chip early">
                              <div className="chip-header">
                                <span className="chip-day">{slot.day.substring(0, 3)}</span>
                                <span className="chip-capacity">{slot.capacity}%</span>
                              </div>
                              <span className="chip-time">{formatTime(slot.time)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Lunch Hour Alternatives */}
                    {lunchTimes.length > 0 && (
                      <div className="insight-section lunch-hour">
                        <div className="insight-section-header">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
                            <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
                            <line x1="6" y1="1" x2="6" y2="4"></line>
                            <line x1="10" y1="1" x2="10" y2="4"></line>
                            <line x1="14" y1="1" x2="14" y2="4"></line>
                          </svg>
                          <h5>Lunch Break Friendly</h5>
                        </div>
                        <p className="insight-description">Quick visit options 11am-2pm</p>
                        <div className="insight-times">
                          {lunchTimes.map((slot) => (
                            <div key={slot.datetime} className="insight-time-chip lunch">
                              <div className="chip-header">
                                <span className="chip-day">{slot.day.substring(0, 3)}</span>
                                <span className="chip-capacity">{slot.capacity}%</span>
                              </div>
                              <span className="chip-time">{formatTime(slot.time)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Evening Options */}
                    {eveningTimes.length > 0 && (
                      <div className="insight-section evening">
                        <div className="insight-section-header">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                          </svg>
                          <h5>Evening Visits</h5>
                        </div>
                        <p className="insight-description">After-work options after 6pm</p>
                        <div className="insight-times">
                          {eveningTimes.map((slot) => (
                            <div key={slot.datetime} className="insight-time-chip evening">
                              <div className="chip-header">
                                <span className="chip-day">{slot.day.substring(0, 3)}</span>
                                <span className="chip-capacity">{slot.capacity}%</span>
                              </div>
                              <span className="chip-time">{formatTime(slot.time)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Best Hours - Absolute Quietest */}
                    {bestHours.length > 0 && (
                      <div className="insight-section best-hours">
                        <div className="insight-section-header">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                          </svg>
                          <h5>Best Hours Overall</h5>
                        </div>
                        <p className="insight-description">The absolute quietest times to visit</p>
                        <div className="insight-times">
                          {bestHours.map((slot) => (
                            <div key={slot.datetime} className="insight-time-chip best">
                              <div className="chip-header">
                                <span className="chip-day">{slot.day.substring(0, 3)}</span>
                                <span className="chip-capacity">{slot.capacity}%</span>
                              </div>
                              <span className="chip-time">{formatTime(slot.time)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="weekly-insights-footer">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    <span>Find the perfect time that fits your schedule</span>
                  </div>
                </div>
              );
            })()}
          </>
        )}

        {/* Planner Tab */}
        {busynessSubTab === 'planner' && (
          <>
            {/* Planner Header */}
            <div className="planner-header">
              <div className="planner-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <div>
                  <h3>Visit Planner</h3>
                  <p>Find your perfect time to visit</p>
                </div>
              </div>
            </div>

            {/* Quick Presets */}
            <div className="quick-presets-card">
              <div className="presets-header">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polyline>
                </svg>
                <h4>Quick Scenarios</h4>
              </div>
              <div className="presets-grid">
                <button className="preset-btn" onClick={() => applyPreset('early-bird')}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="5"></circle>
                    <line x1="12" y1="1" x2="12" y2="3"></line>
                    <line x1="12" y1="21" x2="12" y2="23"></line>
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                  </svg>
                  <div className="preset-info">
                    <span className="preset-title">Early Bird</span>
                    <span className="preset-desc">Weekday mornings</span>
                  </div>
                </button>

                <button className="preset-btn" onClick={() => applyPreset('lunch-break')}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <div className="preset-info">
                    <span className="preset-title">Lunch Break</span>
                    <span className="preset-desc">12pm-2pm</span>
                  </div>
                </button>

                <button className="preset-btn" onClick={() => applyPreset('after-work')}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                  </svg>
                  <div className="preset-info">
                    <span className="preset-title">After Work</span>
                    <span className="preset-desc">Weekday evenings</span>
                  </div>
                </button>

                <button className="preset-btn" onClick={() => applyPreset('weekend-warrior')}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                  </svg>
                  <div className="preset-info">
                    <span className="preset-title">Weekend Morning</span>
                    <span className="preset-desc">Quietest weekends</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Collapsible Filter Controls */}
            <div className="planner-filters-card">
              <button className="customize-toggle" onClick={() => setShowCustomFilters(!showCustomFilters)}>
                <div className="customize-toggle-content">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 3h-6l-2 3h-4l-2-3H2l4 18h12l4-18z"></path>
                    <path d="M2 3h20M6 6l-1.5 6.5M18 6l1.5 6.5"></path>
                  </svg>
                  <div>
                    <h4>Customize Your Preferences</h4>
                    <p>Fine-tune your search with advanced filters</p>
                  </div>
                </div>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    transform: showCustomFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                  }}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>

              {showCustomFilters && (
                <div className="custom-filters-content">
                  {/* Day Preference */}
                  <div className="filter-group">
                    <label className="filter-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      Day Selection
                    </label>
                    <div className="filter-options">
                      <button
                        className={`filter-option ${plannerDayPreference === 'any' ? 'active' : ''}`}
                        onClick={() => {
                          setPlannerDayPreference('any');
                          setPlannerSpecificDays([]);
                        }}
                      >
                        Any Day
                      </button>
                      <button
                        className={`filter-option ${plannerDayPreference === 'weekday' ? 'active' : ''}`}
                        onClick={() => {
                          setPlannerDayPreference('weekday');
                          setPlannerSpecificDays([]);
                        }}
                      >
                        Weekdays
                      </button>
                      <button
                        className={`filter-option ${plannerDayPreference === 'weekend' ? 'active' : ''}`}
                        onClick={() => {
                          setPlannerDayPreference('weekend');
                          setPlannerSpecificDays([]);
                        }}
                      >
                        Weekends
                      </button>
                      <button
                        className={`filter-option ${plannerDayPreference === 'specific' ? 'active' : ''}`}
                        onClick={() => setPlannerDayPreference('specific')}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                          <line x1="8" y1="14" x2="8" y2="14"></line>
                          <line x1="12" y1="14" x2="12" y2="14"></line>
                          <line x1="16" y1="14" x2="16" y2="14"></line>
                        </svg>
                        Choose Days
                      </button>
                    </div>

                    {/* Specific Day Selector */}
                    {plannerDayPreference === 'specific' && (
                      <div className="specific-days-selector">
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                          <button
                            key={day}
                            className={`day-chip ${plannerSpecificDays.includes(day) ? 'selected' : ''}`}
                            onClick={() => {
                              if (plannerSpecificDays.includes(day)) {
                                setPlannerSpecificDays(plannerSpecificDays.filter((d) => d !== day));
                              } else {
                                setPlannerSpecificDays([...plannerSpecificDays, day]);
                              }
                            }}
                          >
                            {day.substring(0, 3)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Time Preference */}
                  <div className="filter-group">
                    <label className="filter-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                      Time Range
                    </label>
                    <div className="filter-options">
                      <button
                        className={`filter-option ${plannerTimePreference === 'any' ? 'active' : ''}`}
                        onClick={() => {
                          setPlannerTimePreference('any');
                          setPlannerTimeRangeStart(6);
                          setPlannerTimeRangeEnd(22);
                        }}
                      >
                        Any Time
                      </button>
                      <button
                        className={`filter-option ${plannerTimePreference === 'morning' ? 'active' : ''}`}
                        onClick={() => {
                          setPlannerTimePreference('morning');
                          setPlannerTimeRangeStart(6);
                          setPlannerTimeRangeEnd(12);
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="5"></circle>
                          <line x1="12" y1="1" x2="12" y2="3"></line>
                          <line x1="12" y1="21" x2="12" y2="23"></line>
                          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                          <line x1="1" y1="12" x2="3" y2="12"></line>
                          <line x1="21" y1="12" x2="23" y2="12"></line>
                          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                        </svg>
                        Morning
                      </button>
                      <button
                        className={`filter-option ${plannerTimePreference === 'afternoon' ? 'active' : ''}`}
                        onClick={() => {
                          setPlannerTimePreference('afternoon');
                          setPlannerTimeRangeStart(12);
                          setPlannerTimeRangeEnd(18);
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="4"></circle>
                          <path d="M12 2v2"></path>
                          <path d="M12 20v2"></path>
                          <path d="m4.93 4.93 1.41 1.41"></path>
                          <path d="m17.66 17.66 1.41 1.41"></path>
                          <path d="M2 12h2"></path>
                          <path d="M20 12h2"></path>
                          <path d="m6.34 17.66-1.41 1.41"></path>
                          <path d="m19.07 4.93-1.41 1.41"></path>
                        </svg>
                        Afternoon
                      </button>
                      <button
                        className={`filter-option ${plannerTimePreference === 'evening' ? 'active' : ''}`}
                        onClick={() => {
                          setPlannerTimePreference('evening');
                          setPlannerTimeRangeStart(18);
                          setPlannerTimeRangeEnd(23);
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                        </svg>
                        Evening
                      </button>
                      <button
                        className={`filter-option ${plannerTimePreference === 'custom' ? 'active' : ''}`}
                        onClick={() => setPlannerTimePreference('custom')}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        Custom Range
                      </button>
                    </div>

                    {/* Custom Time Range Slider */}
                    {plannerTimePreference === 'custom' && (
                      <div className="time-range-selector">
                        <div className="time-range-display">
                          <span className="range-label">From:</span>
                          <span className="range-value">{plannerTimeRangeStart}:00</span>
                          <span className="range-label">To:</span>
                          <span className="range-value">{plannerTimeRangeEnd}:00</span>
                        </div>

                        <div className="range-sliders">
                          <div className="range-slider-group">
                            <label>Start Time</label>
                            <input
                              type="range"
                              min="0"
                              max="23"
                              value={plannerTimeRangeStart}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (val < plannerTimeRangeEnd) {
                                  setPlannerTimeRangeStart(val);
                                }
                              }}
                              className="time-slider"
                            />
                            <div className="slider-ticks">
                              <span>12am</span>
                              <span>6am</span>
                              <span>12pm</span>
                              <span>6pm</span>
                              <span>11pm</span>
                            </div>
                          </div>

                          <div className="range-slider-group">
                            <label>End Time</label>
                            <input
                              type="range"
                              min="0"
                              max="23"
                              value={plannerTimeRangeEnd}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (val > plannerTimeRangeStart) {
                                  setPlannerTimeRangeEnd(val);
                                }
                              }}
                              className="time-slider"
                            />
                            <div className="slider-ticks">
                              <span>12am</span>
                              <span>6am</span>
                              <span>12pm</span>
                              <span>6pm</span>
                              <span>11pm</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Crowd Tolerance */}
                  <div className="filter-group">
                    <label className="filter-label">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      Crowd Preference
                    </label>
                    <div className="filter-options">
                      <button
                        className={`filter-option ${plannerCrowdTolerance === 'quiet' ? 'active' : ''}`}
                        onClick={() => setPlannerCrowdTolerance('quiet')}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="8.5" cy="7" r="4"></circle>
                        </svg>
                        Quietest
                      </button>
                      <button
                        className={`filter-option ${plannerCrowdTolerance === 'moderate' ? 'active' : ''}`}
                        onClick={() => setPlannerCrowdTolerance('moderate')}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        Moderate Crowds OK
                      </button>
                      <button
                        className={`filter-option ${plannerCrowdTolerance === 'any' ? 'active' : ''}`}
                        onClick={() => setPlannerCrowdTolerance('any')}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                          <circle cx="20" cy="8" r="2"></circle>
                        </svg>
                        Any Crowd Level
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Personalized Recommendations */}
            {(() => {
              const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
              const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
              const weekendDays = ['Saturday', 'Sunday'];

              // Determine which days to check based on preference
              let daysToCheck = allDays;
              if (plannerDayPreference === 'weekday') daysToCheck = weekdays;
              if (plannerDayPreference === 'weekend') daysToCheck = weekendDays;
              if (plannerDayPreference === 'specific' && plannerSpecificDays.length > 0)
                daysToCheck = plannerSpecificDays;

              // Collect all matching times
              const allMatchingTimes = [];

              daysToCheck.forEach((day) => {
                const dayData = getDayCapacities(day + ' 00:00:00');
                dayData.forEach(([datetime, capacity]) => {
                  if (Number(capacity) === 0) return; // Skip closed times

                  const time = datetime.split(' ')[1];
                  const hour = parseInt(time.split(':')[0]);
                  const capacityNum = Number(capacity);

                  // Filter by time range (works for both preset and custom)
                  let matchesTime = true;
                  if (plannerTimePreference !== 'any') {
                    if (hour < plannerTimeRangeStart || hour >= plannerTimeRangeEnd) {
                      matchesTime = false;
                    }
                  }

                  if (!matchesTime) return;

                  // Filter by crowd tolerance
                  let matchesCrowd = true;
                  if (plannerCrowdTolerance === 'quiet' && capacityNum >= 40) matchesCrowd = false;
                  if (plannerCrowdTolerance === 'moderate' && capacityNum >= 70) matchesCrowd = false;

                  if (!matchesCrowd) return;

                  allMatchingTimes.push({
                    day,
                    time: time.substring(0, 5),
                    capacity: capacityNum,
                    datetime,
                    hour,
                  });
                });
              });

              // Calculate alternatives (for both no results and to show more options)
              const calculateAlternatives = () => {
                const alternatives = [];

                // Try relaxing crowd tolerance
                if (plannerCrowdTolerance === 'quiet') {
                  let moderateCount = 0;
                  daysToCheck.forEach((day) => {
                    const dayData = getDayCapacities(day + ' 00:00:00');
                    dayData.forEach(([datetime, capacity]) => {
                      const time = datetime.split(' ')[1];
                      const hour = parseInt(time.split(':')[0]);
                      const capacityNum = Number(capacity);

                      if (capacityNum === 0) return;

                      let matchesTime = true;
                      if (plannerTimePreference !== 'any') {
                        if (hour < plannerTimeRangeStart || hour >= plannerTimeRangeEnd) matchesTime = false;
                      }

                      if (matchesTime && capacityNum < 70) moderateCount++;
                    });
                  });

                  if (moderateCount > allMatchingTimes.length) {
                    alternatives.push({
                      change: 'Try "Moderate Crowds OK" instead',
                      count: moderateCount,
                      action: () => setPlannerCrowdTolerance('moderate'),
                    });
                  }
                }

                if (plannerCrowdTolerance === 'moderate') {
                  let anyCount = 0;
                  daysToCheck.forEach((day) => {
                    const dayData = getDayCapacities(day + ' 00:00:00');
                    dayData.forEach(([datetime, capacity]) => {
                      const time = datetime.split(' ')[1];
                      const hour = parseInt(time.split(':')[0]);
                      const capacityNum = Number(capacity);

                      if (capacityNum === 0) return;

                      let matchesTime = true;
                      if (plannerTimePreference !== 'any') {
                        if (hour < plannerTimeRangeStart || hour >= plannerTimeRangeEnd) matchesTime = false;
                      }

                      if (matchesTime) anyCount++;
                    });
                  });

                  if (anyCount > allMatchingTimes.length) {
                    alternatives.push({
                      change: 'Try "Any Crowd Level" for all options',
                      count: anyCount,
                      action: () => setPlannerCrowdTolerance('any'),
                    });
                  }
                }

                // Try expanding time range
                if (plannerTimePreference !== 'any') {
                  let expandedCount = 0;
                  daysToCheck.forEach((day) => {
                    const dayData = getDayCapacities(day + ' 00:00:00');
                    dayData.forEach(([datetime, capacity]) => {
                      const capacityNum = Number(capacity);
                      if (capacityNum === 0) return;

                      let matchesCrowd = true;
                      if (plannerCrowdTolerance === 'quiet' && capacityNum >= 40) matchesCrowd = false;
                      if (plannerCrowdTolerance === 'moderate' && capacityNum >= 70) matchesCrowd = false;

                      if (matchesCrowd) expandedCount++;
                    });
                  });

                  if (expandedCount > allMatchingTimes.length) {
                    alternatives.push({
                      change: 'Try "Any Time" to see all available slots',
                      count: expandedCount,
                      action: () => {
                        setPlannerTimePreference('any');
                        setPlannerTimeRangeStart(6);
                        setPlannerTimeRangeEnd(22);
                      },
                    });
                  }
                }

                // Try expanding days
                if (plannerDayPreference !== 'any') {
                  let anyDayCount = 0;
                  allDays.forEach((day) => {
                    const dayData = getDayCapacities(day + ' 00:00:00');
                    dayData.forEach(([datetime, capacity]) => {
                      const time = datetime.split(' ')[1];
                      const hour = parseInt(time.split(':')[0]);
                      const capacityNum = Number(capacity);

                      if (capacityNum === 0) return;

                      let matchesTime = true;
                      if (plannerTimePreference !== 'any') {
                        if (hour < plannerTimeRangeStart || hour >= plannerTimeRangeEnd) matchesTime = false;
                      }

                      let matchesCrowd = true;
                      if (plannerCrowdTolerance === 'quiet' && capacityNum >= 40) matchesCrowd = false;
                      if (plannerCrowdTolerance === 'moderate' && capacityNum >= 70) matchesCrowd = false;

                      if (matchesTime && matchesCrowd) anyDayCount++;
                    });
                  });

                  if (anyDayCount > allMatchingTimes.length) {
                    alternatives.push({
                      change: 'Try "Any Day" for more options',
                      count: anyDayCount,
                      action: () => {
                        setPlannerDayPreference('any');
                        setPlannerSpecificDays([]);
                      },
                    });
                  }
                }

                return alternatives;
              };

              const alternatives = calculateAlternatives();

              if (allMatchingTimes.length === 0) {
                return (
                  <div className="planner-no-results">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <h4>No Matches Found</h4>
                    <p>Your filters are too strict. Try these alternatives:</p>

                    {alternatives.length > 0 ? (
                      <div className="alternatives-list">
                        {alternatives.map((alt, idx) => (
                          <div key={idx} className="alternative-item" onClick={alt.action}>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                            <div className="alternative-content">
                              <span className="alternative-text">{alt.change}</span>
                              <span className="alternative-count">+{alt.count} options</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-alternatives">Try selecting different preferences above</p>
                    )}
                  </div>
                );
              }

              // Sort by capacity (best times first)
              const sortedTimes = allMatchingTimes.sort((a, b) => a.capacity - b.capacity);

              // Get top recommendations
              const topRecommendations = sortedTimes.slice(0, 8);

              // Group by day for alternative view
              const timesByDay = {};
              sortedTimes.slice(0, 20).forEach((item) => {
                if (!timesByDay[item.day]) timesByDay[item.day] = [];
                if (timesByDay[item.day].length < 3) {
                  timesByDay[item.day].push(item);
                }
              });

              return (
                <>
                  {/* Summary Stats */}
                  <div className="planner-summary">
                    <div className="summary-stat">
                      <div className="stat-number">{allMatchingTimes.length}</div>
                      <div className="stat-label">Matching Times</div>
                    </div>
                    <div className="summary-stat">
                      <div className="stat-number">
                        {Math.round(
                          sortedTimes.slice(0, 5).reduce((sum, t) => sum + t.capacity, 0) /
                            Math.min(5, sortedTimes.length)
                        )}
                        %
                      </div>
                      <div className="stat-label">Avg Crowd Level</div>
                    </div>
                    <div className="summary-stat">
                      <div className="stat-number">{Object.keys(timesByDay).length}</div>
                      <div className="stat-label">Days Available</div>
                    </div>
                  </div>

                  {/* Top Recommendations */}
                  <div className="planner-recommendations">
                    <div className="planner-section-header">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                      </svg>
                      <h4>Your Best Times</h4>
                      <p>Top recommendations based on your preferences</p>
                    </div>

                    <div className="planner-times-grid">
                      {topRecommendations.map((slot, idx) => (
                        <div key={slot.datetime} className="planner-time-card">
                          <div className="planner-rank">#{idx + 1}</div>
                          <div className="planner-day-badge">{slot.day.substring(0, 3)}</div>
                          <div className="planner-time">{slot.time}</div>
                          <div className="planner-capacity-bar">
                            <div
                              className="planner-capacity-fill"
                              style={{
                                width: `${slot.capacity}%`,
                                background: slot.capacity < 30 ? '#10b981' : slot.capacity < 60 ? '#f59e0b' : '#ef4444',
                              }}
                            ></div>
                          </div>
                          <div className="planner-capacity-text">{slot.capacity}% Full</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* By Day Breakdown */}
                  {Object.keys(timesByDay).length > 0 && (
                    <div className="planner-by-day">
                      <div className="planner-section-header">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                        <h4>By Day</h4>
                        <p>Best times for each day</p>
                      </div>

                      <div className="planner-day-grid">
                        {Object.entries(timesByDay).map(([day, times]: [string, any[]]) => (
                          <div key={day} className="planner-day-card">
                            <div className="planner-day-header">
                              <span className="planner-day-name">{day}</span>
                              <span className="planner-day-count">{times.length} options</span>
                            </div>
                            <div className="planner-day-times">
                              {times.map((slot) => (
                                <div key={slot.datetime} className="planner-mini-chip">
                                  <span className="mini-chip-time">{slot.time}</span>
                                  <span className="mini-chip-capacity">{slot.capacity}%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Planning Tips */}
                  <div className="planner-tips">
                    <div className="tip-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                      </svg>
                    </div>
                    <div className="tip-content">
                      <h5>Pro Tips</h5>
                      <ul>
                        {plannerCrowdTolerance === 'quiet' && (
                          <li>Arrive early or during off-peak hours for the best experience</li>
                        )}
                        {plannerTimePreference === 'morning' && (
                          <li>Morning visits typically have the freshest stock and shortest queues</li>
                        )}
                        {plannerTimePreference === 'evening' && (
                          <li>Evening visits can be quieter but may have limited stock</li>
                        )}
                        {plannerDayPreference === 'weekend' && (
                          <li>Weekends tend to be busier - consider arriving right at opening</li>
                        )}
                        {plannerDayPreference === 'weekday' && (
                          <li>Weekdays are typically quieter, especially mid-morning</li>
                        )}
                        <li>Crowd levels can vary - check "Crowd Analyser" tab for real-time updates</li>
                      </ul>
                    </div>
                  </div>

                  {/* Show alternatives even when there are results */}
                  {alternatives.length > 0 && (
                    <div className="planner-suggestions">
                      <div className="suggestions-header">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                          <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                          <line x1="12" y1="22.08" x2="12" y2="12"></line>
                        </svg>
                        <h5>Want more options?</h5>
                      </div>
                      <div className="alternatives-list">
                        {alternatives.map((alt, idx) => (
                          <div key={idx} className="alternative-item" onClick={alt.action}>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                            <div className="alternative-content">
                              <span className="alternative-text">{alt.change}</span>
                              <span className="alternative-count">+{alt.count - allMatchingTimes.length} more</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </>
        )}

        {/* Planner 2 Tab */}
        {busynessSubTab === 'planner2' && (
          <>
            {/* Smart Visit Planner Card */}
            <div className="smart-visit-planner-card">
              <div className="planner-card-header">
                <div className="planner-card-title">
                  <div className="planner-icon-wrapper">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                  </div>
                  <div className="planner-title-content">
                    <h3>Smart Visit Planner</h3>
                    <p>Get personalized recommendations for the best times to visit</p>
                  </div>
                </div>
              </div>

              <div className="planner-card-content">
                <div className="planner-description">
                  <p>
                    Configure your ideal visit preferences to get personalized recommendations for the best times to
                    visit this location.
                  </p>
                </div>

                <button className="preferences-button-primary" onClick={() => setShowPreferencesModal(true)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 3h-6l-2 3h-4l-2-3H2l4 18h12l4-18z"></path>
                    <path d="M2 3h20M6 6l-1.5 6.5M18 6l1.5 6.5"></path>
                  </svg>
                  <span>Customize Your Preferences</span>
                </button>
              </div>
            </div>

            {/* Recommendations based on saved preferences */}
            {(() => {
              const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
              const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
              const weekendDays = ['Saturday', 'Sunday'];

              // Determine which days to check based on saved preferences
              let daysToCheck = allDays;
              if (savedPreferences.dayPreference === 'weekday') daysToCheck = weekdays;
              if (savedPreferences.dayPreference === 'weekend') daysToCheck = weekendDays;
              if (savedPreferences.dayPreference === 'specific' && savedPreferences.specificDays.length > 0)
                daysToCheck = savedPreferences.specificDays;

              // Collect all matching times
              const allMatchingTimes = [];

              daysToCheck.forEach((day) => {
                const dayData = getDayCapacities(day + ' 00:00:00');
                dayData.forEach(([datetime, capacity]) => {
                  if (Number(capacity) === 0) return; // Skip closed times

                  const time = datetime.split(' ')[1];
                  const hour = parseInt(time.split(':')[0]);
                  const capacityNum = Number(capacity);

                  // Filter by time range
                  let matchesTime = true;
                  if (savedPreferences.timePreference !== 'any') {
                    if (hour < savedPreferences.timeRangeStart || hour >= savedPreferences.timeRangeEnd) {
                      matchesTime = false;
                    }
                  }

                  if (!matchesTime) return;

                  // Filter by crowd tolerance
                  let matchesCrowd = true;
                  if (savedPreferences.crowdTolerance === 'quiet' && capacityNum >= 40) matchesCrowd = false;
                  if (savedPreferences.crowdTolerance === 'moderate' && capacityNum >= 70) matchesCrowd = false;

                  if (!matchesCrowd) return;

                  allMatchingTimes.push({
                    day,
                    time: time.substring(0, 5),
                    capacity: capacityNum,
                    datetime,
                    hour,
                  });
                });
              });

              // Sort by capacity (best times first)
              const sortedTimes = allMatchingTimes.sort((a, b) => a.capacity - b.capacity);
              const topRecommendations = sortedTimes.slice(0, 8);

              if (allMatchingTimes.length === 0) {
                // Get busy times for display
                const busyTimes = [];
                daysToCheck.forEach((day) => {
                  const dayData = getDayCapacities(day + ' 00:00:00');
                  dayData.forEach(([datetime, capacity]) => {
                    if (Number(capacity) === 0) return; // Skip closed times

                    const time = datetime.split(' ')[1];
                    const hour = parseInt(time.split(':')[0]);
                    const capacityNum = Number(capacity);

                    // Filter by time range
                    let matchesTime = true;
                    if (savedPreferences.timePreference !== 'any') {
                      if (hour < savedPreferences.timeRangeStart || hour >= savedPreferences.timeRangeEnd) {
                        matchesTime = false;
                      }
                    }

                    if (!matchesTime) return;

                    busyTimes.push({
                      day,
                      time: time.substring(0, 5),
                      capacity: capacityNum,
                      datetime,
                      hour,
                    });
                  });
                });

                // Sort by capacity (busiest first) and take top 2
                const sortedBusyTimes = busyTimes.sort((a, b) => b.capacity - a.capacity).slice(0, 2);

                return (
                  <>
                    <div className="planner-no-results">
                      <div className="no-results-header">
                        <svg
                          width="64"
                          height="64"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <div className="no-results-content">
                          <h4>No Quiet Times Found</h4>
                          <p>This location tends to be busy during your preferred times.</p>
                        </div>
                      </div>

                      {/* Show Busy Times */}
                      {sortedBusyTimes.length > 0 && (
                        <div className="busy-times-section">
                          <div className="busy-times-header">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                              <circle cx="9" cy="7" r="4"></circle>
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                            <div>
                              <h5>This is Busy</h5>
                              <p>These times are particularly crowded during your preferred period</p>
                            </div>
                          </div>

                          <div className="planner-times-grid">
                            {sortedBusyTimes.map((slot, idx) => (
                              <div key={slot.datetime} className="planner-time-card busy">
                                <div className="planner-rank">#{idx + 1}</div>
                                <div className="planner-day-badge">{slot.day.substring(0, 3)}</div>
                                <div className="planner-time">{slot.time}</div>
                                <div className="planner-capacity-bar">
                                  <div
                                    className="planner-capacity-fill"
                                    style={{
                                      width: `${slot.capacity}%`,
                                      background:
                                        slot.capacity < 30 ? '#10b981' : slot.capacity < 60 ? '#f59e0b' : '#ef4444',
                                    }}
                                  ></div>
                                </div>
                                <div className="planner-capacity-text">{slot.capacity}% Full</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Alternative Suggestions */}
                    {(() => {
                      const alternatives = [];

                      // Check if we can expand time range
                      const expandedTimes = [];
                      const expandedStart = Math.max(0, savedPreferences.timeRangeStart - 2);
                      const expandedEnd = Math.min(23, savedPreferences.timeRangeEnd + 2);

                      if (
                        expandedStart < savedPreferences.timeRangeStart ||
                        expandedEnd > savedPreferences.timeRangeEnd
                      ) {
                        daysToCheck.forEach((day) => {
                          const dayData = getDayCapacities(day + ' 00:00:00');
                          dayData.forEach(([datetime, capacity]) => {
                            if (Number(capacity) === 0) return;

                            const time = datetime.split(' ')[1];
                            const hour = parseInt(time.split(':')[0]);
                            const capacityNum = Number(capacity);

                            if (hour >= expandedStart && hour < expandedEnd) {
                              let matchesCrowd = true;
                              if (savedPreferences.crowdTolerance === 'quiet' && capacityNum >= 40)
                                matchesCrowd = false;
                              if (savedPreferences.crowdTolerance === 'moderate' && capacityNum >= 70)
                                matchesCrowd = false;

                              if (matchesCrowd) {
                                expandedTimes.push({
                                  day,
                                  time: time.substring(0, 5),
                                  capacity: capacityNum,
                                  datetime,
                                  hour,
                                });
                              }
                            }
                          });
                        });

                        const expandedSorted = expandedTimes.sort((a, b) => a.capacity - b.capacity);
                        if (expandedSorted.length > 0) {
                          alternatives.push({
                            change: 'Expand time range by 2 hours',
                            count: expandedSorted.length,
                            action: () => {
                              setSavedPreferences((prev) => ({
                                ...prev,
                                timeRangeStart: expandedStart,
                                timeRangeEnd: expandedEnd,
                                timePreference: 'custom',
                              }));
                            },
                          });
                        }
                      }

                      // Check if we can relax crowd tolerance
                      if (savedPreferences.crowdTolerance !== 'any') {
                        const relaxedTimes = [];
                        const relaxedTolerance = savedPreferences.crowdTolerance === 'quiet' ? 'moderate' : 'any';

                        daysToCheck.forEach((day) => {
                          const dayData = getDayCapacities(day + ' 00:00:00');
                          dayData.forEach(([datetime, capacity]) => {
                            if (Number(capacity) === 0) return;

                            const time = datetime.split(' ')[1];
                            const hour = parseInt(time.split(':')[0]);
                            const capacityNum = Number(capacity);

                            if (hour >= savedPreferences.timeRangeStart && hour < savedPreferences.timeRangeEnd) {
                              let matchesCrowd = true;
                              if (relaxedTolerance === 'moderate' && capacityNum >= 70) matchesCrowd = false;

                              if (matchesCrowd) {
                                relaxedTimes.push({
                                  day,
                                  time: time.substring(0, 5),
                                  capacity: capacityNum,
                                  datetime,
                                  hour,
                                });
                              }
                            }
                          });
                        });

                        const relaxedSorted = relaxedTimes.sort((a, b) => a.capacity - b.capacity);
                        if (relaxedSorted.length > 0) {
                          alternatives.push({
                            change: `Accept ${relaxedTolerance === 'moderate' ? 'moderate crowds' : 'any crowd level'}`,
                            count: relaxedSorted.length,
                            action: () => {
                              setSavedPreferences((prev) => ({
                                ...prev,
                                crowdTolerance: relaxedTolerance,
                              }));
                            },
                          });
                        }
                      }

                      // Check if we can include more days
                      if (savedPreferences.dayPreference !== 'any') {
                        const allDaysTimes = [];
                        const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

                        allDays.forEach((day) => {
                          const dayData = getDayCapacities(day + ' 00:00:00');
                          dayData.forEach(([datetime, capacity]) => {
                            if (Number(capacity) === 0) return;

                            const time = datetime.split(' ')[1];
                            const hour = parseInt(time.split(':')[0]);
                            const capacityNum = Number(capacity);

                            if (hour >= savedPreferences.timeRangeStart && hour < savedPreferences.timeRangeEnd) {
                              let matchesCrowd = true;
                              if (savedPreferences.crowdTolerance === 'quiet' && capacityNum >= 40)
                                matchesCrowd = false;
                              if (savedPreferences.crowdTolerance === 'moderate' && capacityNum >= 70)
                                matchesCrowd = false;

                              if (matchesCrowd) {
                                allDaysTimes.push({
                                  day,
                                  time: time.substring(0, 5),
                                  capacity: capacityNum,
                                  datetime,
                                  hour,
                                });
                              }
                            }
                          });
                        });

                        const allDaysSorted = allDaysTimes.sort((a, b) => a.capacity - b.capacity);
                        if (allDaysSorted.length > 0) {
                          alternatives.push({
                            change: 'Include all days of the week',
                            count: allDaysSorted.length,
                            action: () => {
                              setSavedPreferences((prev) => ({
                                ...prev,
                                dayPreference: 'any',
                              }));
                            },
                          });
                        }
                      }

                      if (alternatives.length > 0) {
                        return (
                          <div className="alternatives-section">
                            <div className="alternatives-header">
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M9 12l2 2 4-4"></path>
                                <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"></path>
                                <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"></path>
                                <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3"></path>
                                <path d="M12 21c0-1 1-3 3-3s3 2 3 3-1 3-3 3-3-2-3-3"></path>
                              </svg>
                              <h4>Want More Options?</h4>
                              <p>Try these adjustments to see more recommendations</p>
                            </div>

                            <div className="alternatives-list">
                              {alternatives.map((alt, idx) => (
                                <div key={idx} className="alternative-item" onClick={alt.action}>
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                  </svg>
                                  <div className="alternative-content">
                                    <span className="alternative-text">{alt.change}</span>
                                    <span className="alternative-count">+{alt.count} more</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }

                      return null;
                    })()}
                  </>
                );
              }

              return (
                <>
                  {/* Top Recommendations */}
                  <div className="planner-recommendations">
                    <div className="planner-section-header">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                      </svg>
                      <h4>Your Best Times</h4>
                      <p>Top recommendations based on your saved preferences</p>
                    </div>

                    <div className="planner-times-grid">
                      {topRecommendations.map((slot, idx) => (
                        <div key={slot.datetime} className="planner-time-card">
                          <div className="planner-rank">#{idx + 1}</div>
                          <div className="planner-day-badge">{slot.day.substring(0, 3)}</div>
                          <div className="planner-time">{slot.time}</div>
                          <div className="planner-capacity-bar">
                            <div
                              className="planner-capacity-fill"
                              style={{
                                width: `${slot.capacity}%`,
                                background: slot.capacity < 30 ? '#10b981' : slot.capacity < 60 ? '#f59e0b' : '#ef4444',
                              }}
                            ></div>
                          </div>
                          <div className="planner-capacity-text">{slot.capacity}% Full</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Alternative Suggestions */}
                  {(() => {
                    const alternatives = [];

                    // Check if we can expand time range
                    const expandedTimes = [];
                    const expandedStart = Math.max(0, savedPreferences.timeRangeStart - 2);
                    const expandedEnd = Math.min(23, savedPreferences.timeRangeEnd + 2);

                    if (
                      expandedStart < savedPreferences.timeRangeStart ||
                      expandedEnd > savedPreferences.timeRangeEnd
                    ) {
                      daysToCheck.forEach((day) => {
                        const dayData = getDayCapacities(day + ' 00:00:00');
                        dayData.forEach(([datetime, capacity]) => {
                          if (Number(capacity) === 0) return;

                          const time = datetime.split(' ')[1];
                          const hour = parseInt(time.split(':')[0]);
                          const capacityNum = Number(capacity);

                          if (hour >= expandedStart && hour < expandedEnd) {
                            let matchesCrowd = true;
                            if (savedPreferences.crowdTolerance === 'quiet' && capacityNum >= 40) matchesCrowd = false;
                            if (savedPreferences.crowdTolerance === 'moderate' && capacityNum >= 70)
                              matchesCrowd = false;

                            if (matchesCrowd) {
                              expandedTimes.push({
                                day,
                                time: time.substring(0, 5),
                                capacity: capacityNum,
                                datetime,
                                hour,
                              });
                            }
                          }
                        });
                      });

                      const expandedSorted = expandedTimes.sort((a, b) => a.capacity - b.capacity);
                      if (expandedSorted.length > allMatchingTimes.length) {
                        alternatives.push({
                          change: 'Expand time range by 2 hours',
                          count: expandedSorted.length,
                          action: () => {
                            setSavedPreferences((prev) => ({
                              ...prev,
                              timeRangeStart: expandedStart,
                              timeRangeEnd: expandedEnd,
                              timePreference: 'custom',
                            }));
                          },
                        });
                      }
                    }

                    // Check if we can relax crowd tolerance
                    if (savedPreferences.crowdTolerance !== 'any') {
                      const relaxedTimes = [];
                      const relaxedTolerance = savedPreferences.crowdTolerance === 'quiet' ? 'moderate' : 'any';

                      daysToCheck.forEach((day) => {
                        const dayData = getDayCapacities(day + ' 00:00:00');
                        dayData.forEach(([datetime, capacity]) => {
                          if (Number(capacity) === 0) return;

                          const time = datetime.split(' ')[1];
                          const hour = parseInt(time.split(':')[0]);
                          const capacityNum = Number(capacity);

                          if (hour >= savedPreferences.timeRangeStart && hour < savedPreferences.timeRangeEnd) {
                            let matchesCrowd = true;
                            if (relaxedTolerance === 'moderate' && capacityNum >= 70) matchesCrowd = false;

                            if (matchesCrowd) {
                              relaxedTimes.push({
                                day,
                                time: time.substring(0, 5),
                                capacity: capacityNum,
                                datetime,
                                hour,
                              });
                            }
                          }
                        });
                      });

                      const relaxedSorted = relaxedTimes.sort((a, b) => a.capacity - b.capacity);
                      if (relaxedSorted.length > allMatchingTimes.length) {
                        alternatives.push({
                          change: `Accept ${relaxedTolerance === 'moderate' ? 'moderate crowds' : 'any crowd level'}`,
                          count: relaxedSorted.length,
                          action: () => {
                            setSavedPreferences((prev) => ({
                              ...prev,
                              crowdTolerance: relaxedTolerance,
                            }));
                          },
                        });
                      }
                    }

                    // Check if we can include more days
                    if (savedPreferences.dayPreference !== 'any') {
                      const allDaysTimes = [];
                      const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

                      allDays.forEach((day) => {
                        const dayData = getDayCapacities(day + ' 00:00:00');
                        dayData.forEach(([datetime, capacity]) => {
                          if (Number(capacity) === 0) return;

                          const time = datetime.split(' ')[1];
                          const hour = parseInt(time.split(':')[0]);
                          const capacityNum = Number(capacity);

                          if (hour >= savedPreferences.timeRangeStart && hour < savedPreferences.timeRangeEnd) {
                            let matchesCrowd = true;
                            if (savedPreferences.crowdTolerance === 'quiet' && capacityNum >= 40) matchesCrowd = false;
                            if (savedPreferences.crowdTolerance === 'moderate' && capacityNum >= 70)
                              matchesCrowd = false;

                            if (matchesCrowd) {
                              allDaysTimes.push({
                                day,
                                time: time.substring(0, 5),
                                capacity: capacityNum,
                                datetime,
                                hour,
                              });
                            }
                          }
                        });
                      });

                      const allDaysSorted = allDaysTimes.sort((a, b) => a.capacity - b.capacity);
                      if (allDaysSorted.length > allMatchingTimes.length) {
                        alternatives.push({
                          change: 'Include all days of the week',
                          count: allDaysSorted.length,
                          action: () => {
                            setSavedPreferences((prev) => ({
                              ...prev,
                              dayPreference: 'any',
                            }));
                          },
                        });
                      }
                    }

                    if (alternatives.length > 0) {
                      return (
                        <div className="alternatives-section">
                          <div className="alternatives-header">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M9 12l2 2 4-4"></path>
                              <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3"></path>
                              <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3"></path>
                              <path d="M12 3c0 1-1 3-3 3s-3-2-3-3 1-3 3-3 3 2 3 3"></path>
                              <path d="M12 21c0-1 1-3 3-3s3 2 3 3-1 3-3 3-3-2-3-3"></path>
                            </svg>
                            <h4>Want More Options?</h4>
                            <p>Try these adjustments to see more recommendations</p>
                          </div>

                          <div className="alternatives-list">
                            {alternatives.map((alt, idx) => (
                              <div key={idx} className="alternative-item" onClick={alt.action}>
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                                <div className="alternative-content">
                                  <span className="alternative-text">{alt.change}</span>
                                  <span className="alternative-count">+{alt.count - allMatchingTimes.length} more</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }

                    return null;
                  })()}

                  {/* Summary Stats */}
                  <div className="planner-summary">
                    <div className="summary-stat">
                      <div className="stat-number">{allMatchingTimes.length}</div>
                      <div className="stat-label">Matching Times</div>
                    </div>
                    <div className="summary-stat">
                      <div className="stat-number">
                        {Math.round(
                          sortedTimes.slice(0, 5).reduce((sum, t) => sum + t.capacity, 0) /
                            Math.min(5, sortedTimes.length)
                        )}
                        %
                      </div>
                      <div className="stat-label">Avg Crowd Level</div>
                    </div>
                    <div className="summary-stat">
                      <div className="stat-number">{new Set(sortedTimes.map((t) => t.day)).size}</div>
                      <div className="stat-label">Days Available</div>
                    </div>
                  </div>
                </>
              );
            })()}
          </>
        )}
      </div>

      {/* Preferences Modal */}
      {createPortal(
        <AnimatePresence>
          {showPreferencesModal && (
            <PreferencesModal
              isOpen={showPreferencesModal}
              onClose={() => setShowPreferencesModal(false)}
              preferences={savedPreferences}
              onSave={(newPreferences) => setSavedPreferences(newPreferences)}
            />
          )}
        </AnimatePresence>,
        document.body
      )}
    </motion.div>
  );
};

// Preferences Modal Component
const PreferencesModal = ({ isOpen, onClose, preferences, onSave }) => {
  const [tempPreferences, setTempPreferences] = useState(preferences);

  useEffect(() => {
    if (isOpen) {
      setTempPreferences(preferences);
    }
  }, [isOpen, preferences]);

  const handleSave = () => {
    onSave(tempPreferences);
    onClose();
  };

  const applyPreset = (preset: string) => {
    switch (preset) {
      case 'early-bird':
        setTempPreferences((prev) => ({
          ...prev,
          dayPreference: 'weekday',
          timePreference: 'morning',
          timeRangeStart: 6,
          timeRangeEnd: 12,
          crowdTolerance: 'quiet',
          specificDays: [],
          preset: 'early-bird',
        }));
        break;
      case 'weekday-lunch':
        setTempPreferences((prev) => ({
          ...prev,
          dayPreference: 'weekday',
          timePreference: 'custom',
          timeRangeStart: 12,
          timeRangeEnd: 14,
          crowdTolerance: 'moderate',
          specificDays: [],
          preset: 'weekday-lunch',
        }));
        break;
      case 'weekend-lunch':
        setTempPreferences((prev) => ({
          ...prev,
          dayPreference: 'weekend',
          timePreference: 'custom',
          timeRangeStart: 12,
          timeRangeEnd: 14,
          crowdTolerance: 'moderate',
          specificDays: [],
          preset: 'weekend-lunch',
        }));
        break;
      case 'after-work':
        setTempPreferences((prev) => ({
          ...prev,
          dayPreference: 'weekday',
          timePreference: 'custom',
          timeRangeStart: 18,
          timeRangeEnd: 20,
          crowdTolerance: 'moderate',
          specificDays: [],
          preset: 'after-work',
        }));
        break;
      case 'weekend-warrior':
        setTempPreferences((prev) => ({
          ...prev,
          dayPreference: 'weekend',
          timePreference: 'morning',
          timeRangeStart: 6,
          timeRangeEnd: 12,
          crowdTolerance: 'quiet',
          specificDays: [],
          preset: 'weekend-warrior',
        }));
        break;
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="preferences-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="preferences-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="preferences-modal-header">
          <div className="preferences-modal-title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 3h-6l-2 3h-4l-2-3H2l4 18h12l4-18z"></path>
              <path d="M2 3h20M6 6l-1.5 6.5M18 6l1.5 6.5"></path>
            </svg>
            <div>
              <h3>Visit Preferences</h3>
              <p>Customize your ideal visit experience</p>
            </div>
          </div>
          <button onClick={handleSave} className="save-preferences-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17,21 17,13 7,13 7,21"></polyline>
              <polyline points="7,3 7,8 15,8"></polyline>
            </svg>
            <span>Save</span>
          </button>
        </div>

        {/* Modal Content */}
        <div className="preferences-modal-content">
          {/* Quick Scenarios */}
          <div className="quick-presets-card">
            <div className="presets-header">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polyline>
              </svg>
              <h4>Quick Scenarios</h4>
            </div>
            <div className="presets-grid">
              <button
                className={`preset-btn ${tempPreferences.preset === 'early-bird' ? 'active' : ''}`}
                onClick={() => applyPreset('early-bird')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="5"></circle>
                  <line x1="12" y1="1" x2="12" y2="3"></line>
                  <line x1="12" y1="21" x2="12" y2="23"></line>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                </svg>
                <div className="preset-info">
                  <span className="preset-title">Early Bird</span>
                  <span className="preset-desc">Weekday mornings</span>
                </div>
              </button>

              <button
                className={`preset-btn ${tempPreferences.preset === 'weekday-lunch' ? 'active' : ''}`}
                onClick={() => applyPreset('weekday-lunch')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <div className="preset-info">
                  <span className="preset-title">Weekday Lunch</span>
                  <span className="preset-desc">12pm-2pm weekdays</span>
                </div>
              </button>

              <button
                className={`preset-btn ${tempPreferences.preset === 'weekend-lunch' ? 'active' : ''}`}
                onClick={() => applyPreset('weekend-lunch')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <div className="preset-info">
                  <span className="preset-title">Weekend Lunch</span>
                  <span className="preset-desc">12pm-2pm weekends</span>
                </div>
              </button>

              <button
                className={`preset-btn ${tempPreferences.preset === 'after-work' ? 'active' : ''}`}
                onClick={() => applyPreset('after-work')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                </svg>
                <div className="preset-info">
                  <span className="preset-title">After Work</span>
                  <span className="preset-desc">Weekday evenings</span>
                </div>
              </button>

              <button
                className={`preset-btn ${tempPreferences.preset === 'weekend-warrior' ? 'active' : ''}`}
                onClick={() => applyPreset('weekend-warrior')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                </svg>
                <div className="preset-info">
                  <span className="preset-title">Weekend Morning</span>
                  <span className="preset-desc">Quietest weekends</span>
                </div>
              </button>
            </div>
          </div>

          {/* Custom Preferences */}
          <div className="preferences-filters-card">
            <h4>Custom Preferences</h4>

            {/* Day Preference */}
            <div className="filter-group">
              <label className="filter-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                Day Selection
              </label>
              <div className="filter-options">
                <button
                  className={`filter-option ${tempPreferences.dayPreference === 'any' ? 'active' : ''}`}
                  onClick={() => setTempPreferences((prev) => ({ ...prev, dayPreference: 'any', specificDays: [] }))}
                >
                  Any Day
                </button>
                <button
                  className={`filter-option ${tempPreferences.dayPreference === 'weekday' ? 'active' : ''}`}
                  onClick={() =>
                    setTempPreferences((prev) => ({ ...prev, dayPreference: 'weekday', specificDays: [] }))
                  }
                >
                  Weekdays
                </button>
                <button
                  className={`filter-option ${tempPreferences.dayPreference === 'weekend' ? 'active' : ''}`}
                  onClick={() =>
                    setTempPreferences((prev) => ({ ...prev, dayPreference: 'weekend', specificDays: [] }))
                  }
                >
                  Weekends
                </button>
                <button
                  className={`filter-option ${tempPreferences.dayPreference === 'specific' ? 'active' : ''}`}
                  onClick={() => setTempPreferences((prev) => ({ ...prev, dayPreference: 'specific' }))}
                >
                  Choose Days
                </button>
              </div>

              {/* Specific Day Selector */}
              {tempPreferences.dayPreference === 'specific' && (
                <div className="specific-days-selector">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                    <button
                      key={day}
                      className={`day-chip ${tempPreferences.specificDays.includes(day) ? 'selected' : ''}`}
                      onClick={() => {
                        if (tempPreferences.specificDays.includes(day)) {
                          setTempPreferences((prev) => ({
                            ...prev,
                            specificDays: prev.specificDays.filter((d) => d !== day),
                          }));
                        } else {
                          setTempPreferences((prev) => ({
                            ...prev,
                            specificDays: [...prev.specificDays, day],
                          }));
                        }
                      }}
                    >
                      {day.substring(0, 3)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Time Preference */}
            <div className="filter-group">
              <label className="filter-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                Time Range
              </label>
              <div className="filter-options">
                <button
                  className={`filter-option ${tempPreferences.timePreference === 'any' ? 'active' : ''}`}
                  onClick={() =>
                    setTempPreferences((prev) => ({
                      ...prev,
                      timePreference: 'any',
                      timeRangeStart: 6,
                      timeRangeEnd: 22,
                    }))
                  }
                >
                  Any Time
                </button>
                <button
                  className={`filter-option ${tempPreferences.timePreference === 'morning' ? 'active' : ''}`}
                  onClick={() =>
                    setTempPreferences((prev) => ({
                      ...prev,
                      timePreference: 'morning',
                      timeRangeStart: 6,
                      timeRangeEnd: 12,
                    }))
                  }
                >
                  Morning
                </button>
                <button
                  className={`filter-option ${tempPreferences.timePreference === 'afternoon' ? 'active' : ''}`}
                  onClick={() =>
                    setTempPreferences((prev) => ({
                      ...prev,
                      timePreference: 'afternoon',
                      timeRangeStart: 12,
                      timeRangeEnd: 18,
                    }))
                  }
                >
                  Afternoon
                </button>
                <button
                  className={`filter-option ${tempPreferences.timePreference === 'evening' ? 'active' : ''}`}
                  onClick={() =>
                    setTempPreferences((prev) => ({
                      ...prev,
                      timePreference: 'evening',
                      timeRangeStart: 18,
                      timeRangeEnd: 23,
                    }))
                  }
                >
                  Evening
                </button>
                <button
                  className={`filter-option ${tempPreferences.timePreference === 'custom' ? 'active' : ''}`}
                  onClick={() => setTempPreferences((prev) => ({ ...prev, timePreference: 'custom' }))}
                >
                  Custom Range
                </button>
              </div>

              {/* Custom Time Range Slider */}
              {tempPreferences.timePreference === 'custom' && (
                <div className="time-range-selector">
                  <div className="time-range-display">
                    <span className="range-label">From:</span>
                    <span className="range-value">{tempPreferences.timeRangeStart}:00</span>
                    <span className="range-label">To:</span>
                    <span className="range-value">{tempPreferences.timeRangeEnd}:00</span>
                  </div>

                  <div className="range-sliders">
                    <div className="range-slider-group">
                      <label>Start Time</label>
                      <input
                        type="range"
                        min="0"
                        max="23"
                        value={tempPreferences.timeRangeStart}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (val < tempPreferences.timeRangeEnd) {
                            setTempPreferences((prev) => ({ ...prev, timeRangeStart: val }));
                          }
                        }}
                        className="time-slider"
                      />
                    </div>

                    <div className="range-slider-group">
                      <label>End Time</label>
                      <input
                        type="range"
                        min="0"
                        max="23"
                        value={tempPreferences.timeRangeEnd}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          if (val > tempPreferences.timeRangeStart) {
                            setTempPreferences((prev) => ({ ...prev, timeRangeEnd: val }));
                          }
                        }}
                        className="time-slider"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Crowd Tolerance */}
            <div className="filter-group">
              <label className="filter-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                Crowd Preference
              </label>
              <div className="filter-options">
                <button
                  className={`filter-option ${tempPreferences.crowdTolerance === 'quiet' ? 'active' : ''}`}
                  onClick={() => setTempPreferences((prev) => ({ ...prev, crowdTolerance: 'quiet' }))}
                >
                  Quietest
                </button>
                <button
                  className={`filter-option ${tempPreferences.crowdTolerance === 'moderate' ? 'active' : ''}`}
                  onClick={() => setTempPreferences((prev) => ({ ...prev, crowdTolerance: 'moderate' }))}
                >
                  Moderate Crowds OK
                </button>
                <button
                  className={`filter-option ${tempPreferences.crowdTolerance === 'any' ? 'active' : ''}`}
                  onClick={() => setTempPreferences((prev) => ({ ...prev, crowdTolerance: 'any' }))}
                >
                  Any Crowd Level
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const ShopsTab = ({ listOfShops, handleShopClick, shops, convertedCurrentDateTime }) => {
  const [shopSearchTerm, setShopSearchTerm] = useState('');
  const [showOpenOnly, setShowOpenOnly] = useState(false);

  // Helper function to get current capacity for a shop
  const getShopCurrentCapacity = (shopId: string) => {
    const shop = shops[shopId];
    if (!shop?.capacities || typeof shop.capacities !== 'object') {
      return null;
    }

    // Check if capacities is empty
    try {
      if (Object.keys(shop.capacities).length === 0) {
        return null;
      }
    } catch (error) {
      console.warn('Error accessing shop capacities keys:', error, shop.capacities);
      return null;
    }

    // Shop capacities might be in ISO format (like "2024-01-15T14:00:00Z")
    // or already converted to day format (like {"Monday": [{time: "14:00:00", capacity: 50}]})

    // First, check if it's already in day-of-week format
    let firstKey;
    try {
      firstKey = Object.keys(shop.capacities)[0];
    } catch (error) {
      console.warn('Error accessing shop capacities keys for firstKey:', error, shop.capacities);
      return null;
    }
    const isDayFormat = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].includes(
      firstKey
    );

    if (isDayFormat) {
      // Already in day-of-week format
      const parts = convertedCurrentDateTime.split(' ');
      if (parts.length < 2) return null;

      const currentDay = parts[0];
      const currentTime = parts[1];

      if (shop.capacities[currentDay]) {
        const timeSlot = shop.capacities[currentDay].find((slot) => slot.time === currentTime);
        return timeSlot ? timeSlot.capacity : null;
      }
    } else {
      // ISO format - need to find matching entry
      // convertedCurrentDateTime is like "Monday 14:00:00"
      // Shop capacities are like {"2024-01-15T14:00:00Z": 50}
      let capacityKey;
      try {
        capacityKey = Object.keys(shop.capacities).find((key) => {
          const day = getDayOfWeek(key.split('T')[0]);
          const time = key.split('T')[1].replace('Z', '');
          const converted = `${day} ${time}`;
          return converted === convertedCurrentDateTime;
        });
      } catch (error) {
        console.warn('Error accessing shop capacities keys for capacityKey:', error, shop.capacities);
        return null;
      }

      if (capacityKey) {
        return shop.capacities[capacityKey];
      }
    }

    return null;
  };

  // Filter shops based on search term and open status
  const filteredShops = listOfShops.filter((shopId) => {
    const matchesSearch = shopId.toLowerCase().includes(shopSearchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (showOpenOnly) {
      const capacity = getShopCurrentCapacity(shopId);
      return capacity !== null && capacity > 0;
    }

    return true;
  });

  // Get open shops count
  const openShopsCount = listOfShops.filter((shopId) => {
    const capacity = getShopCurrentCapacity(shopId);
    return capacity !== null && capacity > 0;
  }).length;

  return (
    <motion.div
      key="shops"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-content"
    >
      <div className="shops-section">
        <div className="shops-header">
          <div className="shops-header-title">
            <h3>Shops & Stores</h3>
            {openShopsCount > 0 && (
              <p className="open-shops-count">
                {openShopsCount} of {listOfShops.length} currently open
              </p>
            )}
          </div>

          {listOfShops.length > 0 && (
            <div className="shops-filters">
              {/* Open Now Toggle */}
              <button
                className={`filter-toggle ${showOpenOnly ? 'active' : ''}`}
                onClick={() => setShowOpenOnly(!showOpenOnly)}
              >
                {showOpenOnly ? '✓ Open Now' : 'Show Open Only'}
              </button>

              {/* Search Bar */}
              <div className="shop-search-container">
                <Search size={18} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search shops by name..."
                  value={shopSearchTerm}
                  onChange={(e) => setShopSearchTerm(e.target.value)}
                  className="shop-search-input"
                />
              </div>
            </div>
          )}
        </div>

        {listOfShops.length > 0 ? (
          <div className="shops-table-container">
            <table className="shops-table">
              <thead>
                <tr>
                  <th>Shop Name</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredShops.map((shopId) => {
                  const shop = shops[shopId];
                  if (!shop) return null;

                  const currentCapacity = getShopCurrentCapacity(shopId);
                  const isOpen = currentCapacity !== null && currentCapacity > 0;

                  return (
                    <tr key={shopId} className="shop-row clickable-row" onClick={() => handleShopClick(shopId)}>
                      <td>
                        <div className="shop-name-cell">
                          <strong>{shopId}</strong>
                          <span className="shop-click-hint">Click to view details</span>
                        </div>
                      </td>
                      <td>
                        <div className="shop-status">
                          {currentCapacity !== null ? (
                            <div className={`status-badge ${isOpen ? 'open' : 'closed'}`}>
                              {isOpen ? (
                                <>
                                  <span className="status-dot"></span>
                                  Open • {currentCapacity}% busy
                                </>
                              ) : (
                                <>
                                  <span className="status-dot"></span>
                                  Closed
                                </>
                              )}
                            </div>
                          ) : (
                            <span className="status-unknown">Unknown</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-shops-found">
            <Store size={48} />
            <p>No shops available at this location.</p>
          </div>
        )}

        {filteredShops.length === 0 && listOfShops.length > 0 && (
          <div className="no-shops-found">
            <Store size={48} />
            {showOpenOnly ? (
              <>
                <p>No shops currently open.</p>
                <p>Try turning off the "Open Now" filter.</p>
              </>
            ) : (
              <>
                <p>No shops found matching your search.</p>
                <p>Try adjusting your search terms.</p>
              </>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export const CommunityTab = ({ renderStars, mockLocation }) => {
  return (
    <motion.div
      key="community"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-content"
    >
      <div className="community-section">
        <h3>Community Recommendations</h3>
        {mockLocation.communityRecommendations.map((rec) => (
          <div key={rec.id} className="recommendation-card">
            <div className="rec-header">
              <span className="username">{rec.username}</span>
              <div className="rec-rating">{renderStars(rec.rating)}</div>
            </div>
            <p className="rec-comment">{rec.comment}</p>
            <div className="rec-details">
              <span>Best time: {rec.bestTimeToVisit}</span>
              <span>Parking: {rec.parkingSuggestion}</span>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export const ParkingTab = ({ details, parkingData = null }) => {
  const [parkingSubTab, setParkingSubTab] = React.useState<'list' | 'map'>('map');
  const [selectedParkingOption, setSelectedParkingOption] = React.useState<any>(null);
  const [expandedCards, setExpandedCards] = React.useState<Set<string>>(new Set());
  const [parkingFilters, setParkingFilters] = React.useState<Set<'carParks' | 'streetParking'>>(
    new Set<'carParks' | 'streetParking'>(['carParks', 'streetParking'])
  );

  useEffect(() => {
    eventLogger('location_tab', { location: details.name, tab: `parking ${parkingSubTab}` });
  }, [parkingSubTab]);

  // Use scroll-to-top utility hook with automatic scrolling on sub-tab changes
  const { scrollToTop } = useScrollToTopOnChange([parkingSubTab]);

  // Helper function to capitalize parking type
  const capitalizeType = (type: string): string => {
    if (!type) return '';
    return type
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getSearchTimeColor = (time: string): string => {
    switch (time.toLowerCase()) {
      case 'quick':
        return '#10b981';
      case 'moderate':
        return '#f59e0b';
      case 'long':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getParkingBusynessColor = (level: string): string => {
    switch (level.toLowerCase()) {
      case 'low':
        return '#10b981';
      case 'moderate':
        return '#f59e0b';
      case 'high':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  return (
    <motion.div
      key="parking"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-content"
    >
      <div className="parking-section">
        {/* Parking Sub-tabs */}
        <div className="parking-sub-tabs">
          <button
            className={`parking-sub-tab ${parkingSubTab === 'map' ? 'active' : ''}`}
            onClick={() => {
              setParkingSubTab('map');
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
              <line x1="8" y1="2" x2="8" y2="18"></line>
              <line x1="16" y1="6" x2="16" y2="22"></line>
            </svg>
            Map View
          </button>
          <button
            className={`parking-sub-tab ${parkingSubTab === 'list' ? 'active' : ''}`}
            onClick={() => {
              setParkingSubTab('list');
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6"></line>
              <line x1="8" y1="12" x2="21" y2="12"></line>
              <line x1="8" y1="18" x2="21" y2="18"></line>
              <line x1="3" y1="6" x2="3.01" y2="6"></line>
              <line x1="3" y1="12" x2="3.01" y2="12"></line>
              <line x1="3" y1="18" x2="3.01" y2="18"></line>
            </svg>
            List View
          </button>
        </div>

        {/* List View */}
        {parkingSubTab === 'list' && (
          <>
            {/* Check if parking data exists for this location */}
            {(() => {
              const locationKey = details._id || details.name;
              const existingParkingData = parkingData || null;

              // Handle different possible data structures
              let parkingArray = [];
              let streetParkingArray = [];
              let carParkArray = [];

              if (Array.isArray(existingParkingData)) {
                parkingArray = existingParkingData;
              } else if (
                existingParkingData &&
                existingParkingData.parking_options &&
                Array.isArray(existingParkingData.parking_options)
              ) {
                parkingArray = existingParkingData.parking_options;
              } else if (existingParkingData && typeof existingParkingData === 'object') {
                // Check for separate street_parking_options array
                if (
                  existingParkingData.street_parking_options &&
                  Array.isArray(existingParkingData.street_parking_options)
                ) {
                  streetParkingArray = existingParkingData.street_parking_options;
                }
                // Check for separate car_park_options array
                if (existingParkingData.car_park_options && Array.isArray(existingParkingData.car_park_options)) {
                  carParkArray = existingParkingData.car_park_options;
                }
              }

              if (
                (parkingArray && parkingArray.length > 0) ||
                (streetParkingArray && streetParkingArray.length > 0) ||
                (carParkArray && carParkArray.length > 0)
              ) {
                // Separate car parks and street parking with more flexible filtering
                // This handles both centreparkinginfo and storeparkinginfo type formats
                const carParkOptions = parkingArray.filter((option) => {
                  if (!option.type) return false;
                  const type = option.type.toLowerCase();
                  return (
                    type.includes('car park') ||
                    type.includes('carpark') ||
                    type.includes('parking lot') ||
                    type.includes('garage') ||
                    type.includes('shopping centre car park') ||
                    type.includes('shopping_centre') ||
                    type.includes('commercial car park') ||
                    type.includes('commercial_car_park') ||
                    type.includes('valet')
                  );
                });

                const streetParkingOptions = parkingArray.filter((option) => {
                  if (!option.type) return false;
                  const type = option.type.toLowerCase();
                  return (
                    type.includes('street') ||
                    type.includes('on-street') ||
                    type.includes('meter') ||
                    type.includes('kerb') ||
                    type.includes('curb') ||
                    type === 'street parking'
                  );
                });

                const otherOptions = parkingArray.filter((option) => {
                  if (!option.type) return true;
                  const type = option.type.toLowerCase();
                  return (
                    !type.includes('car park') &&
                    !type.includes('carpark') &&
                    !type.includes('parking lot') &&
                    !type.includes('garage') &&
                    !type.includes('shopping centre') &&
                    !type.includes('shopping_centre') &&
                    !type.includes('commercial') &&
                    !type.includes('valet') &&
                    !type.includes('street') &&
                    !type.includes('on-street') &&
                    !type.includes('meter') &&
                    !type.includes('kerb') &&
                    !type.includes('curb') &&
                    type !== 'street parking'
                  );
                });

                // If no options found with filtering, show all options in "Other" category
                const finalOtherOptions =
                  otherOptions.length > 0
                    ? otherOptions
                    : carParkOptions.length === 0 && streetParkingOptions.length === 0
                    ? parkingArray
                    : otherOptions;

                // For street parking, use separate street_parking_options array if available
                let finalStreetParkingOptions = streetParkingOptions;
                if (streetParkingArray && streetParkingArray.length > 0) {
                  finalStreetParkingOptions = streetParkingArray;
                } else if (streetParkingOptions.length === 0) {
                  // Fallback: try to find them by name patterns in main parking array
                  finalStreetParkingOptions = parkingArray.filter(
                    (option) =>
                      option.name &&
                      (option.name.toLowerCase().includes('street') ||
                        option.name.toLowerCase().includes('road') ||
                        option.name.toLowerCase().includes('avenue') ||
                        option.name.toLowerCase().includes('lane') ||
                        option.name.toLowerCase().includes('drive') ||
                        option.name.toLowerCase().includes('way') ||
                        option.name.toLowerCase().includes('meter') ||
                        option.name.toLowerCase().includes('kerb') ||
                        option.name.toLowerCase().includes('curb'))
                  );
                }

                return (
                  <div className="parking-options-container">
                    {/* Recommended summary */}
                    {existingParkingData.recommended_summary && (
                      <div className="parking-summary">
                        <h4>💡 Recommendation</h4>
                        <p>{existingParkingData.recommended_summary}</p>
                      </div>
                    )}

                    {/* Parking Cards */}
                    {(carParkArray.length > 0 || finalStreetParkingOptions.length > 0 || parkingArray.length > 0) && (
                      <div className="parking-cards-experimental">
                        {/* Car Parks Section */}
                        {(carParkArray.length > 0 || carParkOptions.length > 0) && (
                          <div className="parking-category-section">
                            <div className="category-header car-park-header">
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                <polyline points="21 15 16 10 5 21"></polyline>
                              </svg>
                              <h5>Car Parks & Garages</h5>
                              <span className="count-badge">
                                {carParkArray.length > 0 ? carParkArray.length : carParkOptions.length}
                              </span>
                            </div>

                            <div className="parking-cards-grid">
                              {(carParkArray.length > 0 ? carParkArray : carParkOptions)
                                .slice(0, 5)
                                .map((option, index) => {
                                  const now = new Date();
                                  const currentHour = now.getHours().toString().padStart(2, '0') + ':00';
                                  const currentHourData = option.hourly_data?.[currentHour] || {
                                    busyness_level: 'Unknown',
                                    parking_search_time: 'Unknown',
                                  };

                                  return (
                                    <div
                                      key={option.id || option.name || index}
                                      className="parking-option-card car-park-card"
                                      onClick={() => setSelectedParkingOption(option)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <div className="parking-card-header">
                                        <div className="parking-card-rank">{option.rank || index + 1}</div>
                                        <div className="parking-card-title">
                                          <h6>{option.name || 'Unknown'}</h6>
                                          <span className="parking-card-type">
                                            {capitalizeType(option.type || 'Car Park')}
                                          </span>
                                        </div>
                                      </div>

                                      <div className="parking-card-metrics">
                                        <div className="parking-metric-row">
                                          <div className="parking-metric-item">
                                            <svg
                                              width="16"
                                              height="16"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                            >
                                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                              <circle cx="12" cy="10" r="3"></circle>
                                            </svg>
                                            <span>{option.distance_m ? `${option.distance_m}m` : 'N/A'}</span>
                                          </div>
                                          <div className="parking-metric-item">
                                            <svg
                                              width="16"
                                              height="16"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                            >
                                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                              <circle cx="9" cy="7" r="4"></circle>
                                              <polyline points="16 11 18 13 22 9"></polyline>
                                            </svg>
                                            <span>
                                              {option.walk_time_min ? `${option.walk_time_min} min walk` : 'N/A'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      {(currentHourData.busyness_level !== 'Unknown' ||
                                        currentHourData.parking_search_time !== 'Unknown') && (
                                        <div className="parking-card-status">
                                          {currentHourData.busyness_level !== 'Unknown' && (
                                            <div className="status-sub-card">
                                              <div className="status-sub-card-heading">Busyness</div>
                                              <div
                                                className="status-indicator"
                                                style={{
                                                  borderColor: getParkingBusynessColor(currentHourData.busyness_level),
                                                  backgroundColor: 'transparent',
                                                  color: getParkingBusynessColor(currentHourData.busyness_level),
                                                }}
                                              >
                                                {currentHourData.busyness_level}
                                              </div>
                                            </div>
                                          )}
                                          {currentHourData.parking_search_time !== 'Unknown' && (
                                            <div className="status-sub-card">
                                              <div className="status-sub-card-heading">Search Time</div>
                                              <div
                                                className="status-indicator"
                                                style={{
                                                  borderColor: getSearchTimeColor(currentHourData.parking_search_time),
                                                  backgroundColor: 'transparent',
                                                  color: getSearchTimeColor(currentHourData.parking_search_time),
                                                }}
                                              >
                                                {currentHourData.parking_search_time}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      <div className="cost-info">💰 {option.cost || 'N/A'}</div>

                                      {/* More Details Section */}
                                      {(option.notes ||
                                        (option.pros && option.pros.length > 0) ||
                                        (option.cons && option.cons.length > 0)) && (
                                        <div className="parking-card-details">
                                          <button
                                            className="parking-details-toggle"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const cardId = option.id || option.name || `card-${index}`;
                                              setExpandedCards((prev) => {
                                                const newSet = new Set(prev);
                                                if (newSet.has(cardId)) {
                                                  newSet.delete(cardId);
                                                } else {
                                                  newSet.add(cardId);
                                                }
                                                return newSet;
                                              });
                                            }}
                                          >
                                            <span>More Details</span>
                                            <svg
                                              width="16"
                                              height="16"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                              style={{
                                                transform: expandedCards.has(
                                                  option.id || option.name || `card-${index}`
                                                )
                                                  ? 'rotate(180deg)'
                                                  : 'rotate(0deg)',
                                                transition: 'transform 0.2s ease',
                                              }}
                                            >
                                              <polyline points="6 9 12 15 18 9"></polyline>
                                            </svg>
                                          </button>
                                          {expandedCards.has(option.id || option.name || `card-${index}`) && (
                                            <div className="parking-details-content">
                                              {option.notes && (
                                                <div className="parking-detail-row">
                                                  <div className="parking-detail-label">Notes:</div>
                                                  <div className="parking-detail-value">{option.notes}</div>
                                                </div>
                                              )}
                                              {option.pros && option.pros.length > 0 && (
                                                <div className="parking-detail-row">
                                                  <div className="parking-detail-label">Pros:</div>
                                                  <div className="parking-detail-value">
                                                    <ul className="parking-detail-list">
                                                      {option.pros.map((pro: string, idx: number) => (
                                                        <li key={idx}>{pro}</li>
                                                      ))}
                                                    </ul>
                                                  </div>
                                                </div>
                                              )}
                                              {option.cons && option.cons.length > 0 && (
                                                <div className="parking-detail-row">
                                                  <div className="parking-detail-label">Cons:</div>
                                                  <div className="parking-detail-value">
                                                    <ul className="parking-detail-list">
                                                      {option.cons.map((con: string, idx: number) => (
                                                        <li key={idx}>{con}</li>
                                                      ))}
                                                    </ul>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      <div className="parking-card-actions" onClick={(e) => e.stopPropagation()}>
                                        {option.booking_url && (
                                          <a
                                            href={option.booking_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="parking-card-button"
                                          >
                                            Book Now
                                          </a>
                                        )}
                                        {option.latitude && option.longitude && (
                                          <a
                                            href={`https://www.google.com/maps/dir/?api=1&destination=${option.latitude},${option.longitude}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="parking-card-button directions-button"
                                          >
                                            Get Directions
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}

                        {/* Street Parking Section */}
                        {finalStreetParkingOptions.length > 0 && (
                          <div className="parking-category-section">
                            <div className="category-header street-parking-header">
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                              </svg>
                              <h5>Street Parking</h5>
                              <span className="count-badge">{finalStreetParkingOptions.length}</span>
                            </div>

                            <div className="parking-cards-grid">
                              {finalStreetParkingOptions.slice(0, 5).map((option, index) => {
                                const now = new Date();
                                const currentHour = now.getHours().toString().padStart(2, '0') + ':00';
                                const currentHourData = option.hourly_data?.[currentHour] || {
                                  busyness_level: 'Unknown',
                                  parking_search_time: 'Unknown',
                                };

                                return (
                                  <div
                                    key={option.id || option.name || index}
                                    className="parking-option-card street-parking-card"
                                    onClick={() => setSelectedParkingOption(option)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <div className="parking-card-header">
                                      <div className="parking-card-rank">{option.rank || index + 1}</div>
                                      <div className="parking-card-title">
                                        <h6>{option.name || 'Unknown Street'}</h6>
                                        <span className="parking-card-type">Street Parking</span>
                                      </div>
                                    </div>

                                    <div className="parking-card-metrics">
                                      <div className="parking-metric-row">
                                        <div className="parking-metric-item">
                                          <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                            <circle cx="12" cy="10" r="3"></circle>
                                          </svg>
                                          <span>{option.distance_m ? `${option.distance_m}m` : 'N/A'}</span>
                                        </div>
                                        <div className="parking-metric-item">
                                          <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                          >
                                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="9" cy="7" r="4"></circle>
                                            <polyline points="16 11 18 13 22 9"></polyline>
                                          </svg>
                                          <span>
                                            {option.walk_time_min ? `${option.walk_time_min} min walk` : 'N/A'}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    {(currentHourData.busyness_level !== 'Unknown' ||
                                      currentHourData.parking_search_time !== 'Unknown') && (
                                      <div className="parking-card-status">
                                        {currentHourData.busyness_level !== 'Unknown' && (
                                          <div className="status-sub-card">
                                            <div className="status-sub-card-heading">Busyness</div>
                                            <div
                                              className="status-indicator"
                                              style={{
                                                borderColor: getParkingBusynessColor(currentHourData.busyness_level),
                                                backgroundColor: 'transparent',
                                                color: getParkingBusynessColor(currentHourData.busyness_level),
                                              }}
                                            >
                                              {currentHourData.busyness_level}
                                            </div>
                                          </div>
                                        )}
                                        {currentHourData.parking_search_time !== 'Unknown' && (
                                          <div className="status-sub-card">
                                            <div className="status-sub-card-heading">Search Time</div>
                                            <div
                                              className="status-indicator"
                                              style={{
                                                borderColor: getSearchTimeColor(currentHourData.parking_search_time),
                                                backgroundColor: 'transparent',
                                                color: getSearchTimeColor(currentHourData.parking_search_time),
                                              }}
                                            >
                                              {currentHourData.parking_search_time}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    <div className="cost-info">
                                      💰 {option.cost || 'Free'}
                                      {(option.time_limits || option.max_duration) &&
                                        ` • ${option.time_limits || option.max_duration}`}
                                    </div>

                                    {/* More Details Section */}
                                    {(option.notes ||
                                      (option.pros && option.pros.length > 0) ||
                                      (option.cons && option.cons.length > 0)) && (
                                      <div className="parking-card-details">
                                        <button
                                          className="parking-details-toggle"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const cardId = option.id || option.name || `street-card-${index}`;
                                            setExpandedCards((prev) => {
                                              const newSet = new Set(prev);
                                              if (newSet.has(cardId)) {
                                                newSet.delete(cardId);
                                              } else {
                                                newSet.add(cardId);
                                              }
                                              return newSet;
                                            });
                                          }}
                                        >
                                          <span>More Details</span>
                                          <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            style={{
                                              transform: expandedCards.has(
                                                option.id || option.name || `street-card-${index}`
                                              )
                                                ? 'rotate(180deg)'
                                                : 'rotate(0deg)',
                                              transition: 'transform 0.2s ease',
                                            }}
                                          >
                                            <polyline points="6 9 12 15 18 9"></polyline>
                                          </svg>
                                        </button>
                                        {expandedCards.has(option.id || option.name || `street-card-${index}`) && (
                                          <div className="parking-details-content">
                                            {option.notes && (
                                              <div className="parking-detail-row">
                                                <div className="parking-detail-label">Notes:</div>
                                                <div className="parking-detail-value">{option.notes}</div>
                                              </div>
                                            )}
                                            {option.pros && option.pros.length > 0 && (
                                              <div className="parking-detail-row">
                                                <div className="parking-detail-label">Pros:</div>
                                                <div className="parking-detail-value">
                                                  <ul className="parking-detail-list">
                                                    {option.pros.map((pro: string, idx: number) => (
                                                      <li key={idx}>{pro}</li>
                                                    ))}
                                                  </ul>
                                                </div>
                                              </div>
                                            )}
                                            {option.cons && option.cons.length > 0 && (
                                              <div className="parking-detail-row">
                                                <div className="parking-detail-label">Cons:</div>
                                                <div className="parking-detail-value">
                                                  <ul className="parking-detail-list">
                                                    {option.cons.map((con: string, idx: number) => (
                                                      <li key={idx}>{con}</li>
                                                    ))}
                                                  </ul>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    <div className="parking-card-actions" onClick={(e) => e.stopPropagation()}>
                                      {option.latitude && option.longitude && (
                                        <a
                                          href={`https://www.google.com/maps/dir/?api=1&destination=${option.latitude},${option.longitude}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="parking-card-button directions-button"
                                        >
                                          Get Directions
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              } else {
                return (
                  <div>
                    <div className="no-parking-data">
                      <Car size={48} />
                      <p>No parking data available for this location.</p>
                      <p>Click the button below to get AI-powered parking recommendations.</p>
                    </div>
                  </div>
                );
              }
            })()}
          </>
        )}

        {/* Map View */}
        {parkingSubTab === 'map' && (
          <>
            {(() => {
              const locationKey = details._id || details.name;
              const existingParkingData = parkingData || null;

              // Handle different possible data structures
              let parkingArray = [];
              let streetParkingArray = [];
              let carParkArray = [];

              if (Array.isArray(existingParkingData)) {
                parkingArray = existingParkingData;
              } else if (
                existingParkingData &&
                existingParkingData.parking_options &&
                Array.isArray(existingParkingData.parking_options)
              ) {
                parkingArray = existingParkingData.parking_options;
              } else if (existingParkingData && typeof existingParkingData === 'object') {
                // Check for separate street_parking_options array
                if (
                  existingParkingData.street_parking_options &&
                  Array.isArray(existingParkingData.street_parking_options)
                ) {
                  streetParkingArray = existingParkingData.street_parking_options;
                }
                // Check for separate car_park_options array
                if (existingParkingData.car_park_options && Array.isArray(existingParkingData.car_park_options)) {
                  carParkArray = existingParkingData.car_park_options;
                }
              }

              const allParkingOptions = [...parkingArray, ...carParkArray, ...streetParkingArray];

              if (allParkingOptions.length > 0) {
                // Helper function to determine if a parking option is street parking
                const isStreetParking = (option: any): boolean => {
                  if (streetParkingArray.includes(option)) return true;
                  if (!option.type) return false;
                  const type = option.type.toLowerCase();
                  return (
                    type.includes('street') ||
                    type.includes('on-street') ||
                    type.includes('meter') ||
                    type.includes('kerb') ||
                    type.includes('curb') ||
                    type === 'street parking'
                  );
                };

                // Helper function to determine if a parking option is a car park
                const isCarPark = (option: any): boolean => {
                  if (carParkArray.includes(option)) return true;
                  if (!option.type) return false;
                  const type = option.type.toLowerCase();
                  return (
                    type.includes('car park') ||
                    type.includes('carpark') ||
                    type.includes('parking lot') ||
                    type.includes('garage') ||
                    type.includes('shopping centre car park') ||
                    type.includes('shopping_centre') ||
                    type.includes('commercial car park') ||
                    type.includes('commercial_car_park') ||
                    type.includes('valet')
                  );
                };

                // Filter parking options based on active filters
                const filteredParkingOptions = allParkingOptions.filter((option) => {
                  const isStreet = isStreetParking(option);
                  const isCar = isCarPark(option);
                  
                  // Show street parking if streetParking filter is active
                  if (isStreet && parkingFilters.has('streetParking')) return true;
                  // Show car parks if carParks filter is active
                  if (isCar && parkingFilters.has('carParks')) return true;
                  // Don't show options that don't match either category
                  return false;
                });

                // Calculate map center from location
                const centerLat = parseFloat(details?.details?.latitude || details?.latitude || 0);
                const centerLng = parseFloat(details?.details?.longitude || details?.longitude || 0);

                // Create custom icons for different parking types
                const createParkingIcon = (type: string) => {
                  const color = type?.toLowerCase().includes('street') ? '#10b981' : '#7c3aed';
                  return L.divIcon({
                    html: `
                                <div style="
                                  background: ${color};
                                  width: 32px;
                                  height: 32px;
                                  border-radius: 50%;
                                  display: flex;
                                  align-items: center;
                                  justify-content: center;
                                  color: white;
                                  font-weight: bold;
                                  font-size: 18px;
                                  border: 3px solid white;
                                  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                                ">
                                  P
                    </div>
                              `,
                    className: 'custom-parking-marker',
                    iconSize: [32, 32],
                    iconAnchor: [16, 16],
                  });
                };

                // Create center location icon
                const centerIcon = L.divIcon({
                  html: `
                              <div style="
                                background: #ef4444;
                                width: 40px;
                                height: 40px;
                                border-radius: 50%;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                color: white;
                                font-weight: bold;
                                font-size: 20px;
                                border: 3px solid white;
                                box-shadow: 0 3px 10px rgba(0,0,0,0.4);
                              ">
                                📍
                              </div>
                            `,
                  className: 'custom-center-marker',
                  iconSize: [40, 40],
                  iconAnchor: [20, 20],
                });

                // Handle legend item click - exclusive filtering (only show the clicked type)
                const handleLegendClick = (filterType: 'carParks' | 'streetParking') => {
                  setParkingFilters(new Set([filterType]));
                };

                // Component to fit bounds for all parking markers
                const FitBounds: React.FC<{ parkingOptions: any[]; centerLat: number; centerLng: number }> = ({ parkingOptions, centerLat, centerLng }) => {
                  const map = useMap();
                  
                  // Create a stable key from parking options coordinates
                  const optionsKey = React.useMemo(() => {
                    return parkingOptions
                      .map(opt => `${parseFloat(opt.latitude)},${parseFloat(opt.longitude)}`)
                      .filter(key => !key.includes('NaN'))
                      .sort()
                      .join('|');
                  }, [parkingOptions.length]);

                  React.useEffect(() => {
                    if (parkingOptions.length === 0) {
                      // If no parking options, just center on the location
                      map.setView([centerLat, centerLng], 15);
                      return;
                    }

                    // Collect all coordinates (parking options + center location)
                    const bounds: L.LatLngExpression[] = [[centerLat, centerLng]];
                    
                    parkingOptions.forEach((option) => {
                      const lat = parseFloat(option.latitude);
                      const lng = parseFloat(option.longitude);
                      if (!isNaN(lat) && !isNaN(lng)) {
                        bounds.push([lat, lng]);
                      }
                    });

                    if (bounds.length > 1) {
                      // Fit bounds with padding (50px on all sides)
                      const boundsGroup = L.latLngBounds(bounds);
                      map.fitBounds(boundsGroup, { padding: [50, 50] });
                    } else {
                      // If only center location, just center on it
                      map.setView([centerLat, centerLng], 15);
                    }
                  }, [map, optionsKey, centerLat, centerLng]);

                  return null;
                };

                return (
                  <div className="parking-map-container">
                    <div className="parking-map-legend">
                      <div 
                        className={`legend-item ${parkingFilters.has('carParks') ? 'active' : ''}`}
                        onClick={() => handleLegendClick('carParks')}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="legend-icon" style={{ background: '#7c3aed' }}>
                          P
                        </div>
                        <span>Car Parks</span>
                      </div>
                      <div 
                        className={`legend-item ${parkingFilters.has('streetParking') ? 'active' : ''}`}
                        onClick={() => handleLegendClick('streetParking')}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="legend-icon" style={{ background: '#10b981' }}>
                          P
                        </div>
                        <span>Street Parking</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-icon" style={{ background: '#ef4444' }}>
                          📍
                        </div>
                        <span>Selected Location</span>
                      </div>
                    </div>

                    <MapContainer
                      center={[centerLat, centerLng]}
                      zoom={15}
                      style={{ height: '500px', width: '100%', borderRadius: '12px' }}
                      zoomControl={true}
                    >
                      <TileLayer
                        url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                        subdomains="abcd"
                        maxZoom={20}
                      />

                      {/* Fit bounds to show all markers */}
                      <FitBounds parkingOptions={filteredParkingOptions} centerLat={centerLat} centerLng={centerLng} />

                      {/* Center Location Marker */}
                      <Marker position={[centerLat, centerLng]} icon={centerIcon}>
                        <Popup>
                          <div className="parking-popup">
                            <strong>{details.name}</strong>
                            <p>Your selected location</p>
                          </div>
                        </Popup>
                      </Marker>

                      {/* Parking Option Markers */}
                      {filteredParkingOptions.map((option, index) => {
                        const lat = parseFloat(option.latitude);
                        const lng = parseFloat(option.longitude);
                        if (isNaN(lat) || isNaN(lng)) return null;

                        return (
                          <Marker
                            key={index}
                            position={[lat, lng]}
                            icon={createParkingIcon(option.type)}
                            eventHandlers={{
                              click: () => {
                                setSelectedParkingOption(option);
                              },
                            }}
                          />
                        );
                      })}
                    </MapContainer>

                    <div className="parking-map-summary">
                      <p>
                        Showing {filteredParkingOptions.length} parking option{filteredParkingOptions.length !== 1 ? 's' : ''}{' '}
                        near {details.name}
                      </p>
                    </div>
                  </div>
                );
              } else {
                return (
                  <div className="no-parking-data">
                    <Car size={48} />
                    <p>No parking data available to display on map.</p>
                  </div>
                );
              }
            })()}
          </>
        )}
      </div>

      {/* Parking Details Modal */}
      {selectedParkingOption && (
        <ParkingLocationsModal
          isOpen={!!selectedParkingOption}
          onClose={() => setSelectedParkingOption(null)}
          location={selectedParkingOption}
        />
      )}
    </motion.div>
  );
};

export const TrafficTab = ({ details }) => {
  // Traffic sub-tab state
  const [trafficSubTab, setTrafficSubTab] = useState<'cameras' | 'incidents'>('cameras');

  // Traffic camera state
  const [trafficCameras, setTrafficCameras] = useState<TrafficCamera[]>([]);
  const [nearbyCameras, setNearbyCameras] = useState<TrafficCamera[]>([]);
  const [isLoadingTraffic, setIsLoadingTraffic] = useState<boolean>(false);

  // Traffic hazard state
  const [trafficHazards, setTrafficHazards] = useState<TrafficHazard[]>([]);
  const [nearbyHazards, setNearbyHazards] = useState<TrafficHazard[]>([]);
  const [isLoadingHazards, setIsLoadingHazards] = useState<boolean>(false);

  useEffect(() => {
    const loadTrafficCameras = async () => {
      if (!details) return;

      setIsLoadingTraffic(true);
      try {
        // Fetch the GeoJSON file from the public directory
        const response = await fetch('/real_traffic_cameras_20250901_220500.geojson');
        if (!response.ok) {
          throw new Error(`Failed to fetch GeoJSON: ${response.status}`);
        }

        const trafficCamerasData = await response.json();

        // Convert GeoJSON features to TrafficCamera format
        const cameras: TrafficCamera[] = trafficCamerasData.features.map((feature: any) => ({
          id: feature.id,
          name: feature.properties.title,
          description: feature.properties.view,
          direction: feature.properties.direction,
          status: 'active' as const, // All cameras in GeoJSON are active
          roadName: feature.properties.title.split('(')[0].trim(),
          lastUpdated: new Date().toISOString(), // Use current time since GeoJSON doesn't have this
          imageUrl: feature.properties.href || '', // Use real href from API
          region: feature.properties.region,
          latitude: feature.geometry.coordinates[1],
          longitude: feature.geometry.coordinates[0],
        }));

        setTrafficCameras(cameras);

        // Always show first 5 cameras for testing
        setNearbyCameras(cameras.slice(0, 5));

        // Find cameras within 2km radius
        const locationLat = details.details?.latitude;
        const locationLon = details.details?.longitude;

        if (locationLat && locationLon) {
          const nearby = cameras.filter((camera: TrafficCamera) => {
            const distance = calculateDistance(locationLat, locationLon, camera.latitude, camera.longitude);
            return distance <= 2; // 2km radius
          });

          // Sort cameras from closest to furthest
          const sortedNearby = nearby.sort((a, b) => {
            const distanceA = calculateDistance(locationLat, locationLon, a.latitude, a.longitude);
            const distanceB = calculateDistance(locationLat, locationLon, b.latitude, b.longitude);
            return distanceA - distanceB;
          });

          console.log(`🚦 Found ${sortedNearby.length} traffic cameras within 2km of location from GeoJSON`);
          console.log(
            '📸 Nearby cameras (sorted by distance):',
            sortedNearby.map(
              (c) => `${c.name} (${calculateDistance(locationLat, locationLon, c.latitude, c.longitude).toFixed(2)}km)`
            )
          );
          setNearbyCameras(sortedNearby);

          // If no nearby cameras found, show first 5 cameras for testing
          if (sortedNearby.length === 0) {
            const testCameras = cameras.slice(0, 5);
            // Sort test cameras by distance too
            const sortedTestCameras = testCameras.sort((a, b) => {
              const distanceA = calculateDistance(locationLat, locationLon, a.latitude, a.longitude);
              const distanceB = calculateDistance(locationLat, locationLon, b.latitude, b.longitude);
              return distanceA - distanceB;
            });
            setNearbyCameras(sortedTestCameras);
          }

          // Cameras already have real href URLs from the API
          setNearbyCameras(nearby);
          console.log('✅ Camera feeds already contain real API URLs');
        } else {
          console.log('❌ No location coordinates found, showing first 5 cameras for testing');
          const testCameras = cameras.slice(0, 5);

          // Sort test cameras by distance from Sydney CBD (default location)
          const sydneyLat = -33.8688;
          const sydneyLon = 151.2093;
          const sortedTestCameras = testCameras.sort((a, b) => {
            const distanceA = calculateDistance(sydneyLat, sydneyLon, a.latitude, a.longitude);
            const distanceB = calculateDistance(sydneyLat, sydneyLon, b.latitude, b.longitude);
            return distanceA - distanceB;
          });

          // Cameras already have real href URLs from the API
          setNearbyCameras(sortedTestCameras);
        }
      } catch (error) {
        console.error('❌ Error loading traffic cameras from GeoJSON:', error);
      } finally {
        setIsLoadingTraffic(false);
      }
    };

    loadTrafficCameras();
  }, [details]);

  // Load traffic hazards from API with bbox filtering
  useEffect(() => {
    const loadTrafficHazards = async () => {
      if (!details) return;

      const locationLat = details.details?.latitude;
      const locationLon = details.details?.longitude;

      if (!locationLat || !locationLon) {
        console.log('❌ No location coordinates available for hazards');
        setNearbyHazards([]);
        return;
      }

      setIsLoadingHazards(true);
      try {
        console.log('🚨 Loading traffic hazards from NSW Transport API with bbox...');

        // Fetch hazards with bbox parameter (API will filter to 2km radius)
        const hazards = await fetchTrafficHazards(locationLat, locationLon);
        setTrafficHazards(hazards);

        // Sort hazards from closest to furthest and add distance
        const hazardsWithDistance = hazards
          .map((hazard) => ({
            ...hazard,
            distance: calculateDistance(locationLat, locationLon, hazard.latitude, hazard.longitude),
          }))
          .sort((a, b) => (a.distance || 0) - (b.distance || 0));

        console.log(`🚨 Found ${hazardsWithDistance.length} traffic hazards within 2km (bbox filtered)`);
        console.log('🚨 Hazards data:', hazardsWithDistance);
        setNearbyHazards(hazardsWithDistance);
      } catch (error) {
        console.error('❌ Error loading traffic hazards:', error);
        setNearbyHazards([]);
      } finally {
        setIsLoadingHazards(false);
      }
    };

    loadTrafficHazards();
  }, [details]);

  return (
    <motion.div
      key="traffic"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-content"
    >
      <div className="traffic-section">
        {/* Traffic Sub-tabs */}
        <div className="traffic-sub-tabs">
          <button
            className={`traffic-sub-tab ${trafficSubTab === 'cameras' ? 'active' : ''}`}
            onClick={() => setTrafficSubTab('cameras')}
          >
            <Camera size={16} />
            Live Cameras
          </button>
          <button
            className={`traffic-sub-tab ${trafficSubTab === 'incidents' ? 'active' : ''}`}
            onClick={() => setTrafficSubTab('incidents')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            Incidents
          </button>
        </div>

        {/* Traffic Cameras Section */}
        {trafficSubTab === 'cameras' && (
          <>
            {isLoadingTraffic ? (
              <div className="traffic-loading-modern">
                <div className="loading-spinner">
                  <Camera size={48} />
                </div>
                <p>Loading traffic cameras...</p>
              </div>
            ) : nearbyCameras.length > 0 ? (
              <>
                {/* Summary Card */}
                <div className="traffic-summary-card">
                  <div className="traffic-summary-icon">
                    <Camera size={24} />
                  </div>
                  <div className="traffic-summary-content">
                    <h4>Live Traffic Cameras</h4>
                    <p>
                      {nearbyCameras.length} camera{nearbyCameras.length !== 1 ? 's' : ''} within 2km
                    </p>
                  </div>
                  <div className="traffic-summary-badge">{nearbyCameras.length}</div>
                </div>

                {/* Camera Cards */}
                <div className="cameras-grid-modern">
                  {nearbyCameras.map((camera) => (
                    <div key={camera.id} className="camera-card-modern">
                      <div className="camera-image-wrapper">
                        {camera.status === 'active' && camera.imageUrl ? (
                          <img
                            src={camera.imageUrl}
                            alt={`${camera.name} traffic camera`}
                            className="camera-feed-image"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = `
                                            <div class="camera-feed-error">
                                              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                                                <circle cx="12" cy="13" r="4"></circle>
                                              </svg>
                                              <p>Feed Unavailable</p>
                                        </div>
                                      `;
                              }
                            }}
                          />
                        ) : (
                          <div className="camera-feed-loading">
                            <Camera size={48} />
                            <p>Loading...</p>
                          </div>
                        )}
                        <div className="live-indicator-badge">
                          <span className="live-dot"></span>
                          LIVE
                        </div>
                      </div>

                      <div className="camera-info-modern">
                        <h4 className="camera-name">{camera.name}</h4>
                        <div className="camera-meta">
                          <span className="camera-meta-item">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                              <circle cx="12" cy="10" r="3"></circle>
                            </svg>
                            {details?.details?.latitude && details?.details?.longitude
                              ? `${calculateDistance(
                                  details.details.latitude,
                                  details.details.longitude,
                                  camera.latitude,
                                  camera.longitude
                                ).toFixed(1)}km`
                              : 'N/A'}
                          </span>
                          <span className="camera-meta-item">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polyline points="9 11 12 14 22 4"></polyline>
                              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                            </svg>
                            {camera.direction}
                          </span>
                        </div>
                        {camera.imageUrl && (
                          <button className="view-feed-button" onClick={() => window.open(camera.imageUrl, '_blank')}>
                            <ExternalLink size={16} />
                            View Full Feed
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="no-traffic-data">
                <div className="no-data-icon">
                  <Camera size={48} />
                </div>
                <h4>No Cameras Nearby</h4>
                <p>No traffic cameras found within 2km of this location</p>
              </div>
            )}
          </>
        )}

        {/* Traffic Hazards Section */}
        {trafficSubTab === 'incidents' && (
          <>
            {console.log('🚨 Rendering incidents tab - Loading:', isLoadingHazards, 'Count:', nearbyHazards.length)}
            {isLoadingHazards ? (
              <div className="traffic-loading-modern">
                <div className="loading-spinner">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  </svg>
                </div>
                <p>Loading traffic incidents...</p>
              </div>
            ) : nearbyHazards.length > 0 ? (
              <>
                {/* Summary Card */}
                <div className="traffic-summary-card hazards">
                  <div className="traffic-summary-icon hazard">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                      <line x1="12" y1="9" x2="12" y2="13"></line>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                  </div>
                  <div className="traffic-summary-content">
                    <h4>Traffic Incidents</h4>
                    <p>
                      {nearbyHazards.length} incident{nearbyHazards.length !== 1 ? 's' : ''} within 2km
                    </p>
                  </div>
                  <div className="traffic-summary-badge hazard">{nearbyHazards.length}</div>
                </div>

                {/* Hazard Cards */}
                <div className="hazards-grid-modern">
                  {nearbyHazards.map((hazard) => (
                    <div key={hazard.id} className="hazard-card-modern">
                      <div className="hazard-card-header">
                        <div className={`hazard-severity-badge ${hazard.severity.toLowerCase()}`}>
                          {hazard.severity}
                        </div>
                        <div className="hazard-distance-badge">{hazard.distance?.toFixed(1)}km</div>
                      </div>

                      <h4 className="hazard-title">{hazard.title}</h4>
                      <p className="hazard-description">{hazard.description}</p>

                      <div className="hazard-info-grid">
                        <div className="hazard-info-item">
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                            <circle cx="12" cy="10" r="3"></circle>
                          </svg>
                          <span>{hazard.roadName}</span>
                        </div>
                        <div className="hazard-info-item">
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <polyline points="9 11 12 14 22 4"></polyline>
                            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                          </svg>
                          <span>{hazard.direction}</span>
                        </div>
                      </div>

                      <div className={`hazard-status-badge ${hazard.status.toLowerCase()}`}>{hazard.status}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="no-traffic-data">
                <div className="no-data-icon success">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <h4>All Clear!</h4>
                <p>No traffic incidents reported in this area</p>
              </div>
            )}
          </>
        )}
      </div>
    </motion.div>
  );
};

export const ChatTab = ({ mockLocation }) => {
  return (
    <motion.div
      key="chat"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="tab-content"
    >
      <AIChatbot location={mockLocation} />
    </motion.div>
  );
};

const LocationModal: React.FC<LocationModalProps> = ({ details, handleClose, shop = null, onShopSelect }) => {
  const [activeTab, setActiveTab] = useState<
    'overview' | 'busyness' | 'community' | 'parking' | 'chat' | 'shops' | 'traffic'
  >('overview');

  const headerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Measure header and tabs height for content positioning
  useEffect(() => {
    const updateHeights = () => {
      if (headerRef.current && tabsRef.current && modalRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        const tabsHeight = tabsRef.current.offsetHeight;
        modalRef.current.style.setProperty('--modal-header-height', `${headerHeight}px`);
        modalRef.current.style.setProperty('--modal-tabs-height', `${tabsHeight}px`);
      }
    };

    updateHeights();
    window.addEventListener('resize', updateHeights);
    return () => window.removeEventListener('resize', updateHeights);
  }, [details.name]); // Recalculate when name changes

  useEffect(() => {
    eventLogger('location_tab', { location: details.name, tab: activeTab });
  }, [activeTab]);

  // Update theme-color for iOS Safari when modal opens
  useEffect(() => {
    updateThemeColor('#7c3aed'); // Purple to match modal header
    return () => {
      updateThemeColor('#ffffff'); // Restore white when modal closes
    };
  }, [details.name]);

  // Use scroll-to-top utility hook with automatic scrolling on tab changes
  const { scrollToTop } = useScrollToTopOnChange([activeTab]);
  const [selectedDay, setSelectedDay] = useState<string>('');
  const [parkingData, setParkingData] = useState<any>(null);
  const {
    convertedCurrentDateTime,
    setConvertedCurrentDateTime,
    selectedDateTimes,
    setSelectedDateTimes,
    setUpDate,
    setUpAllDates,
    getDayCapacities,
    getCurrentCapacity,
  } = useDateHandler();

  // Shop modal state
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);

  useEffect(() => {
    // Validate coordinates before calling getLocalCurrentDateTime
    const lat = details.details?.latitude;
    const lng = details.details?.longitude;
    const isValidCoordinate = (coord: number | undefined | null): boolean => {
      if (coord === undefined || coord === null) return false;
      if (typeof coord !== 'number') return false;
      if (isNaN(coord) || !isFinite(coord)) return false;
      return true;
    };

    const hasValidCoords =
      isValidCoordinate(lat) &&
      isValidCoordinate(lng) &&
      typeof lat === 'number' &&
      typeof lng === 'number' &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180;

    const currentDateTime = hasValidCoords ? getLocalCurrentDateTime(lat!, lng!) : getLocalCurrentDateTime(); // Use default Sydney coordinates if invalid

    setConvertedCurrentDateTime(setUpDate(currentDateTime.dateTime));
    setUpAllDates(details.details?.capacities);
    setSelectedDay(currentDateTime.day);
    if (shop) {
      const shopData = createShopData(shop);
      setSelectedShop(shopData);
      if (onShopSelect) {
        // Pass shop object with name, centreName, and coordinates
        onShopSelect({
          name: shopData.name,
          centreName: shopData.centreName || details.name,
          latitude: shopData.latitude,
          longitude: shopData.longitude,
        });
      }
    }
  }, []);

  useEffect(() => {
    setSelectedDateTimes(getDayCapacities(selectedDay));
  }, [selectedDay]);

  // handles API calls if specific tabs are selected
  useEffect(() => {
    if (activeTab === 'parking' && !parkingData) {
      console.log('🚗 Fetching parking data for location:', details.name);
      getData(`/centreparkinginfo/${details.name}`)
        .then((data) => setParkingData(data))
        .catch(() => console.log('No parking data found'));
    }
  }, [activeTab]);

  const { name, details: locationDetails } = details;
  const capacities = locationDetails?.capacities || {};
  const shops = locationDetails?.shops || {};

  // Get list of shops from location details
  const listOfShops = Object.keys(shops);

  const createShopData = (shopId: string): Shop => {
    const shopData = shops[shopId];
    if (!shopData) {
      console.error('Shop data not found for:', shopId);
      return {
        id: shopId,
        name: shopId,
        centreName: details.name,
        category: 'Unknown',
        latitude: details?.details?.latitude || 0,
        longitude: details?.details?.longitude || 0,
        capacities: {},
      };
    }

    return {
      id: shopId,
      name: shopData.name || shopData.storeName || shopData.title || shopData.shopName || shopId,
      centreName: details.name,
      category: shopData.category || 'Retail Store',
      latitude: shopData.latitude || details?.details?.latitude || 0,
      longitude: shopData.longitude || details?.details?.longitude || 0,
      address: shopData.address || `${shopId}, ${details?.name || 'Location'}`,
      phone: shopData.phone,
      website: shopData.website,
      openingHours: shopData.openingHours,
      description: shopData.description,
      capacities: shopData.capacities || {},
      communityReviews: shopData.communityReviews || shopData.reviews || [],
      parkingOptions: shopData.parkingOptions || [],
    };
  };

  const handleShopClick = (shopId: string) => {
    const shopData = createShopData(shopId);
    setSelectedShop(shopData);
    if (onShopSelect) {
      // Pass shop object with name, centreName, and coordinates
      onShopSelect({
        name: shopData.name,
        centreName: shopData.centreName || details.name,
        latitude: shopData.latitude,
        longitude: shopData.longitude,
      });
    }
  };

  const handleCloseShopModal = () => {
    setSelectedShop(null);
  };

  // Mock data for enhanced features (keeping this for now as it's used in other tabs)
  const mockLocation: Location = {
    _id: locationDetails?._id || '1',
    location: name,
    latitude: locationDetails?.latitude || 0,
    longitude: locationDetails?.longitude || 0,
    category: 'Tourist Attractions' as any,
    busyness: 75,
    vibeRating: 4.2,
    communityRecommendations: [
      {
        id: '1',
        userId: 'user1',
        username: 'SydneyExplorer',
        bestTimeToVisit: 'Early morning (6-8 AM)',
        parkingSuggestion: 'Street parking on George St',
        rating: 5,
        comment: "Best time to visit is early morning when it's less crowded!",
        timestamp: new Date(),
      },
    ],
    parkingOptions: [
      {
        id: '1',
        latitude: locationDetails?.latitude || 0,
        longitude: (locationDetails?.longitude || 0) + 0.001,
        description: 'Street parking on George St',
        rating: 4.5,
        totalRatings: 23,
        suggestedBy: 'Community',
        isOfficial: false,
      },
    ],
    capacities: capacities,
    shops: locationDetails?.shops,
  };

  const getParkTime = (capacity: number) => {
    if (capacity < 40) return '< 1 min';
    if (capacity < 70) return '1 - 3 mins';
    if (capacity < 85) return '3 - 5 mins';
    if (capacity < 95) return '5 - 10 mins';
    return '> 10 mins';
  };

  const getBestTimeToVisit = (capacities: any): string => {
    if (!capacities || Object.keys(capacities).length === 0) return 'Check crowds tab';

    // Get all capacities for the current day
    const dayCapacities = getDayCapacities(convertedCurrentDateTime);

    if (dayCapacities.length === 0) return 'Check crowds tab';

    // Find the time with lowest capacity (least busy)
    const lowestCapacity = dayCapacities.reduce((min, [_, capacity]) => Math.min(min, Number(capacity)), 100);

    if (lowestCapacity < 30) return 'Very quiet - great time!';
    if (lowestCapacity < 50) return 'Low crowds - good time';
    if (lowestCapacity < 70) return 'Moderate crowds';
    return 'Usually busy - check crowds tab';
  };

  const parkTime = getParkTime(getCurrentCapacity());
  const bestTimeToVisit = getBestTimeToVisit(capacities);

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: `Check out ${name} on VibeCheck!`,
          text: `I found this great location with ${getCurrentCapacity()}% crowd level and ${
            mockLocation.vibeRating
          }/5 vibe rating!`,
          url: window.location.href,
        })
        .catch((error) => {
          // Silently handle share cancellation or errors
          if (error.name !== 'AbortError') {
            console.log('Share error:', error);
          }
        });
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={16}
        className={i < Math.floor(rating) ? 'star-filled' : 'star-empty'}
        fill={i < Math.floor(rating) ? '#FFD700' : 'none'}
      />
    ));
  };

  const getBusynessColor = (busyness: number): string => {
    if (busyness < 30) return '#4CAF50'; // Green - Low busyness
    if (busyness < 60) return '#FF9800'; // Orange - Medium busyness
    if (busyness < 80) return '#FF5722'; // Red-Orange - High busyness
    return '#F44336'; // Red - Very high busyness
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay fullscreen"
      onClick={handleClose}
    >
      <motion.div
        ref={modalRef}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="location-modal fullscreen"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div ref={headerRef} className="modal-header fullscreen">
          <div className="header-left">
            <h1 className="location-title">{name}</h1>
          </div>
          <div className="header-right">
            {/* Favorites button hidden for now */}
            {/* <button onClick={() => {}} className="icon-action-button" title="Save">
              <Heart size={18} />
            </button> */}
            <button onClick={handleShare} className="icon-action-button" title="Share">
              <Share2 size={18} />
            </button>
            <button onClick={handleClose} className="icon-action-button close" title="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div ref={tabsRef} className="modal-tabs">
          {[
            { id: 'overview', label: 'Overview', icon: MapPin },
            { id: 'busyness', label: 'Crowds', icon: Clock },
            { id: 'shops', label: 'Shops', icon: Store },
            { id: 'parking', label: 'Parking', icon: Car },
            { id: 'traffic', label: 'Traffic', icon: Camera },
            { id: 'community', label: 'Community', icon: MessageCircle },
            { id: 'chat', label: 'AI Chat', icon: MessageCircle },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-tab={id}
              onClick={() => {
                console.log('🔍 Tab clicked:', id);
                if (id === 'traffic') {
                  console.log('🚦 Traffic tab clicked.');
                }
                setActiveTab(id as any);
              }}
              className={`tab-button ${activeTab === id ? 'active' : ''}`}
            >
              <Icon size={18} />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <motion.div className="modal-content fullscreen">
          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
              <OverviewTab
                getCurrentCapacity={getCurrentCapacity}
                getBusynessColor={getBusynessColor}
                parkTime={parkTime}
                bestTimeToVisit={bestTimeToVisit}
                locationDetails={locationDetails}
                convertedCurrentDateTime={convertedCurrentDateTime}
                isShop={shop !== null}
                getDayCapacities={getDayCapacities}
              />
            )}

            {activeTab === 'busyness' && (
              <BusynessTab
                convertedCurrentDateTime={convertedCurrentDateTime}
                getCurrentCapacity={getCurrentCapacity}
                getParkTime={getParkTime}
                getDayCapacities={getDayCapacities}
                capacities={capacities}
                selectedDay={selectedDay}
                setSelectedDay={setSelectedDay}
                selectedDateTimes={selectedDateTimes}
                name={details.name}
              />
            )}

            {activeTab === 'shops' && (
              <ShopsTab
                listOfShops={listOfShops}
                handleShopClick={handleShopClick}
                shops={shops}
                convertedCurrentDateTime={convertedCurrentDateTime}
              />
            )}

            {activeTab === 'community' && (
              <div className="coming-soon-overlay">
                <div className="coming-soon-content">
                  <div className="coming-soon-icon">🚧</div>
                  <h2>Coming Soon</h2>
                  <p>Community features are currently under development</p>
                </div>
              </div>
            )}

            {activeTab === 'parking' && <ParkingTab details={details} parkingData={parkingData} />}

            {activeTab === 'traffic' && <TrafficTab details={details} />}

            {activeTab === 'chat' && (
              <div className="coming-soon-overlay">
                <div className="coming-soon-content">
                  <div className="coming-soon-icon">🤖</div>
                  <h2>Coming Soon</h2>
                  <p>AI Chat features are currently under development</p>
                </div>
              </div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>

      {/* Shop Modal */}
      {selectedShop && <ShopModal shop={selectedShop} handleClose={handleCloseShopModal} />}
    </motion.div>
  );
};

export default LocationModal;
